import React, { useState, useEffect } from 'react';
import './Dropdown.css';
import LoadingOverlay from '../components/LoadingOverlay';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';

export default function NotaFiscalDropdown() {
  const [notasFiscais, setNotasFiscais] = useState([]);
  const [notasFiltradas, setNotasFiltradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Estados para filtro
  const [filtro, setFiltro] = useState('numero');
  const [valorFiltro, setValorFiltro] = useState('');
  const [dropdownAberto, setDropdownAberto] = useState(false);
  const [visualizarNF, setVisualizarNF] = useState(false);
  const [dadosVisualizar, setDadosVisualizar] = useState(null);
  const [visualizarLoading, setVisualizarLoading] = useState(false);
  
  // Estados para seleção de notas
  const [notasSelecionadas, setNotasSelecionadas] = useState([]);
  
  // Estado para modo de edição no popup
  const [modoEdicao, setModoEdicao] = useState(false);

  // Estados para dados do comprador na visualização da NF
  const [dadosCompradorNF, setDadosCompradorNF] = useState(null);
  const [compradorNFId, setCompradorNFId] = useState(null);
  const [mostrarCompradorNF, setMostrarCompradorNF] = useState(false);
  const [salvandoComprador, setSalvandoComprador] = useState(false);
  
  // Estado para aba ativa (pendentes ou concluidas)
  const [abaAtiva, setAbaAtiva] = useState('pendentes');

  // Estado para tipo de nota fiscal (produtos ou servicos)
  const [tipoNotaFiscal, setTipoNotaFiscal] = useState('produtos');
  // Estado para modal de download (escolha de formato)
  const [baixarModalAberto, setBaixarModalAberto] = useState(false);
  const [notasParaBaixar, setNotasParaBaixar] = useState([]);

  // ========== ESTADOS PARA SIDEBAR DE EMISSÃO ==========
  const [sidebarEmissaoAberto, setSidebarEmissaoAberto] = useState(false);
  const [emissaoEmAndamento, setEmissaoEmAndamento] = useState(false);
  const [emissaoProgresso, setEmissaoProgresso] = useState({ atual: 0, total: 0 });
  const [emissaoResultado, setEmissaoResultado] = useState(null); // { sucesso: [], erro: [] }
  const [notasParaEmitir, setNotasParaEmitir] = useState([]);

  // ========== ESTADOS PARA POPUP DE NOTAS COM ERRO ==========
  const [popupErrosAberto, setPopupErrosAberto] = useState(false);
  const [notasComErro, setNotasComErro] = useState([]);
  const [detalheErroAberto, setDetalheErroAberto] = useState(false);
  const [notaErroDetalhe, setNotaErroDetalhe] = useState(null);
  const [detalheErroLoading, setDetalheErroLoading] = useState(false);
  const [abaErroDetalhe, setAbaErroDetalhe] = useState('erro');
  const [produtoErroForm, setProdutoErroForm] = useState(null);
  const [produtosVendaErro, setProdutosVendaErro] = useState([]);
  const [produtoSelecionadoIdx, setProdutoSelecionadoIdx] = useState(0);
  const [salvandoProdutoErro, setSalvandoProdutoErro] = useState(false);
  const [vendaErroForm, setVendaErroForm] = useState(null);
  const [compradorErroForm, setCompradorErroForm] = useState(null);
  const [salvandoCompradorErro, setSalvandoCompradorErro] = useState(false);

  // Opções de filtro
  const opcoesFiltro = [
    { valor: 'numero', label: 'Número da Nota' },
    { valor: 'clienteId', label: 'ID do Cliente' },
    { valor: 'clienteNome', label: 'Nome do Cliente' },
  { valor: 'valor', label: 'Valor (R$)' },
    { valor: 'status', label: 'Status' }
  ];

  // Helper para obter primeiro campo disponível (normaliza variações de maiúsculas/minúsculas)
  const obter = (obj, ...chaves) => {
    for (const k of chaves) {
      if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
    }
    return undefined;
  };

  // Helper para pegar campos de objetos aninhados (ex: nota.cliente.id ou nota.cliente.Nome)
  const obterAninhado = (obj, ...possiveisPaths) => {
    for (const path of possiveisPaths) {
      if (!obj || !path) continue;
      const partes = path.split('.');
      let cur = obj;
      let ok = true;
      for (const p of partes) {
        if (cur && (cur[p] !== undefined && cur[p] !== null)) {
          cur = cur[p];
        } else {
          ok = false; break;
        }
      }
      if (ok) return cur;
    }
    return undefined;
  };

  // Helpers para formatação BRL
  const formatBRL = (value) => {
    if (value === undefined || value === null || value === '') return '';
    const num = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9-,.]/g, '').replace(',', '.'));
    if (Number.isNaN(num)) return '';
    return 'R$ ' + num.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
  };

  const parseBRLToNumber = (str) => {
    if (str === undefined || str === null) return null;
    const cleaned = String(str).replace(/[^0-9,.-]/g, '').replace(/\./g, '').replace(',', '.');
    const num = parseFloat(cleaned);
    return Number.isNaN(num) ? null : num;
  };

  // Filtrar notas por tipo (produto ou serviço)
  const notasPorTipo = notasFiltradas.filter(nota => {
    const tipoVenda = (nota.tipoVenda ?? nota.TipoVenda ?? '').toLowerCase();
    const tipoDoc = (nota.tipoDocumento ?? nota.TipoDocumento ?? '').toLowerCase();
    if (tipoNotaFiscal === 'produtos') {
      return tipoVenda === 'produto' || tipoDoc === 'nfe' || (!tipoVenda && !tipoDoc);
    } else {
      return tipoVenda === 'servico' || tipoVenda === 'serviço' || tipoDoc === 'nfs-e' || tipoDoc === 'nfse';
    }
  });

  // Filtrar notas por status (pendentes ou concluídas/emitidas)
  const notasPendentes = notasPorTipo.filter(nota => {
    const status = (nota.status ?? nota.Status ?? '').toLowerCase();
    return status.includes('pendente') || status.includes('aguardando') || status === '' || !status.includes('emitida') && !status.includes('autorizada') && !status.includes('concluida') && !status.includes('cancelada');
  });

  const notasConcluidas = notasPorTipo.filter(nota => {
    const status = (nota.status ?? nota.Status ?? '').toLowerCase();
    return status.includes('emitida') || status.includes('autorizada') || status.includes('concluida');
  });

  // Notas da aba atual
  const notasAbaAtual = abaAtiva === 'pendentes' ? notasPendentes : notasConcluidas;

  // Funções para seleção de notas
  const toggleSelecaoNota = (notaId, e) => {
    e.stopPropagation(); // Impedir que abra o popup ao clicar no checkbox
    setNotasSelecionadas(prev => {
      if (prev.includes(notaId)) {
        return prev.filter(id => id !== notaId);
      } else {
        return [...prev, notaId];
      }
    });
  };

  const toggleSelecionarTodas = () => {
    if (notasSelecionadas.length === notasAbaAtual.length) {
      setNotasSelecionadas([]);
    } else {
      const todasIds = notasAbaAtual.map(nota => nota.id ?? nota.Id ?? nota.notaId ?? nota.NotaId ?? nota.numero);
      setNotasSelecionadas(todasIds);
    }
  };

  const isNotaSelecionada = (notaId) => notasSelecionadas.includes(notaId);
  const todasSelecionadas = notasAbaAtual.length > 0 && notasSelecionadas.length === notasAbaAtual.length;

  // Limpar seleção ao trocar de aba
  const handleTrocarAba = (novaAba) => {
    setAbaAtiva(novaAba);
    setNotasSelecionadas([]);
  };

  // Trocar tipo de nota fiscal (produtos ou serviços)
  const handleTrocarTipoNota = (novoTipo) => {
    setTipoNotaFiscal(novoTipo);
    setNotasSelecionadas([]);
    setAbaAtiva('pendentes');
  };

  // Funções do footer de ações
  const handleAutorizarNotas = () => {
    // Abrir sidebar de emissão
    const notasSelecionadasCompletas = notasAbaAtual.filter(nota => {
      const notaId = nota.id ?? nota.Id ?? nota.notaId ?? nota.NotaId ?? nota.numero;
      return notasSelecionadas.includes(notaId);
    });
    setNotasParaEmitir(notasSelecionadasCompletas);
    setEmissaoProgresso({ atual: 0, total: notasSelecionadasCompletas.length });
    setEmissaoResultado(null);
    setEmissaoEmAndamento(false);
    setSidebarEmissaoAberto(true);
  };

  // Função para emitir notas sequencialmente
  const handleEmitirNotas = async () => {
    if (notasParaEmitir.length === 0) return;
    
    setEmissaoEmAndamento(true);
    setEmissaoProgresso({ atual: 0, total: notasParaEmitir.length });
    
    const resultadoSucesso = [];
    const resultadoErro = [];
    
    for (let i = 0; i < notasParaEmitir.length; i++) {
      const nota = notasParaEmitir[i];
      const notaId = nota.id ?? nota.Id ?? nota.notaId ?? nota.NotaId;
      const vendaId = nota.vendaId ?? nota.VendaId ?? nota.DadosVenda?.Id ?? nota.DadosVenda?.id;
      
      try {
        // Chamar endpoint de autorização
        const resultado = await apiCall(`NotaFiscal/autorizar/${vendaId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (resultado && resultado.success) {
          resultadoSucesso.push({ ...nota, mensagem: resultado.message || 'Autorizada com sucesso' });
        } else {
          resultadoErro.push({ ...nota, mensagem: resultado?.message || 'Erro desconhecido na autorização' });
        }
      } catch (err) {
        console.error(`Erro ao autorizar nota ${notaId}:`, err);
        resultadoErro.push({ ...nota, mensagem: err.message || 'Erro de conexão' });
      }
      
      // Atualizar progresso
      setEmissaoProgresso({ atual: i + 1, total: notasParaEmitir.length });
    }
    
    setEmissaoResultado({ sucesso: resultadoSucesso, erro: resultadoErro });
    setEmissaoEmAndamento(false);
    
    // Recarregar lista de notas fiscais
    fetchNotasFiscais();
    setNotasSelecionadas([]);
  };

  // Função para fechar sidebar
  const handleFecharSidebarEmissao = () => {
    if (emissaoEmAndamento) return; // Não fechar durante emissão
    setSidebarEmissaoAberto(false);
    setNotasParaEmitir([]);
    setEmissaoResultado(null);
    setEmissaoProgresso({ atual: 0, total: 0 });
  };

  // Função para abrir popup de notas com erro
  const handleVerNotasComErro = () => {
    if (!emissaoResultado || !emissaoResultado.erro || emissaoResultado.erro.length === 0) return;
    
    const notasErroDetalhes = emissaoResultado.erro.map(n => ({
      id: n.id ?? n.Id ?? n.notaId ?? n.NotaId,
      numero: n.numero ?? n.Numero ?? '',
      nomeCliente: n.nomeCliente ?? n.NomeCliente ?? n.nome ?? '',
      valor: n.valor ?? n.Valor ?? n.valorTotal ?? n.ValorTotal ?? null,
      status: n.status ?? n.Status ?? 'Erro',
      mensagem: n.mensagem || 'Erro na emissão',
      vendaId: n.vendaId ?? n.VendaId ?? null
    }));
    
    setNotasComErro(notasErroDetalhes);
    setPopupErrosAberto(true);
  };

  // Abrir detalhe de uma nota com erro
  const handleAbrirDetalheErro = async (notaErro) => {
    setDetalheErroLoading(true);
    setDetalheErroAberto(true);
    setNotaErroDetalhe(null);

    try {
      // Buscar dados completos da nota fiscal
      let detalhe = null;
      if (notaErro.id) {
        try {
          detalhe = await apiCall(`NotaFiscal/${notaErro.id}`);
        } catch (err) {
          console.warn('Erro ao buscar detalhe da nota:', err);
        }
      }

      // Buscar dados da venda se disponível
      const vendaId = detalhe?.VendaId ?? detalhe?.vendaId ?? notaErro.vendaId;
      let vendaDetalhe = null;
      if (vendaId) {
        try {
          vendaDetalhe = await apiCall(`Vendas/${vendaId}`);
        } catch (err) {
          console.warn('Erro ao buscar venda vinculada:', err);
        }
      }

      // Buscar dados do comprador se disponível
      let compradorDetalhe = null;
      if (vendaId) {
        try {
          compradorDetalhe = await apiCall(`DadosCompradorVenda/por-venda/${vendaId}`);
        } catch (err) {
          console.warn('Dados do comprador não encontrados:', err);
        }
      }

      // Montar objeto completo
      const notaCompleta = {
        ...notaErro,
        ...(detalhe || {}),
        vendaDetalhe: vendaDetalhe || null,
        compradorDetalhe: compradorDetalhe || null,
        // Campos de erro
        xMotivo: detalhe?.XMotivo ?? detalhe?.xMotivo ?? '',
        codStatusResposta: detalhe?.CodStatusResposta ?? detalhe?.codStatusResposta ?? '',
        status: detalhe?.Status ?? detalhe?.status ?? notaErro.status ?? 'Erro',
        mensagemErro: notaErro.mensagem || detalhe?.XMotivo || detalhe?.xMotivo || 'Erro desconhecido',
        // Campos fiscais
        numero: detalhe?.Numero ?? detalhe?.numero ?? notaErro.numero ?? '',
        serie: detalhe?.Serie ?? detalhe?.serie ?? '',
        chaveAcesso: detalhe?.ChaveAcesso ?? detalhe?.chaveAcesso ?? '',
        protocoloAutorizacao: detalhe?.ProtocoloAutorizacao ?? detalhe?.protocoloAutorizacao ?? '',
        dataEmissao: detalhe?.DataEmissao ?? detalhe?.dataEmissao ?? '',
        ambiente: detalhe?.Ambiente ?? detalhe?.ambiente ?? '',
        valorTotal: detalhe?.ValorTotal ?? detalhe?.valorTotal ?? vendaDetalhe?.valorFinal ?? vendaDetalhe?.ValorFinal ?? notaErro.valor ?? null,
        nomeCliente: vendaDetalhe?.nomeCliente ?? vendaDetalhe?.NomeCliente ?? notaErro.nomeCliente ?? '',
        // Comprador
        compradorNome: compradorDetalhe?.NomeRazaoSocial ?? compradorDetalhe?.nomeRazaoSocial ?? '',
        compradorDocumento: compradorDetalhe?.NumeroDocumento ?? compradorDetalhe?.numeroDocumento ?? '',
        compradorTipoDoc: compradorDetalhe?.TipoDocumento ?? compradorDetalhe?.tipoDocumento ?? '',
        compradorUF: compradorDetalhe?.UF ?? compradorDetalhe?.uf ?? '',
        compradorMunicipio: compradorDetalhe?.NomeMunicipio ?? compradorDetalhe?.nomeMunicipio ?? '',
        compradorEndereco: compradorDetalhe?.Logradouro ?? compradorDetalhe?.logradouro ?? '',
        compradorCep: compradorDetalhe?.CEP ?? compradorDetalhe?.cep ?? '',
      };

      setNotaErroDetalhe(notaCompleta);

      // Buscar produtos da venda para a aba Produto
      const produtoIds = new Set();
      if (vendaDetalhe?.produtoId ?? vendaDetalhe?.ProdutoId) {
        produtoIds.add(vendaDetalhe.produtoId ?? vendaDetalhe.ProdutoId);
      }
      const itens = vendaDetalhe?.items ?? vendaDetalhe?.Items ?? vendaDetalhe?.itens ?? vendaDetalhe?.Itens ?? [];
      itens.forEach(item => {
        const pid = item.produtoId ?? item.ProdutoId;
        if (pid) produtoIds.add(pid);
      });

      const produtosFetched = [];
      for (const pid of produtoIds) {
        try {
          const prod = await apiCall(`Produto/${pid}`);
          if (prod) produtosFetched.push(prod);
        } catch (err) { console.warn('Erro ao buscar produto:', pid, err); }
      }
      setProdutosVendaErro(produtosFetched);
      setProdutoSelecionadoIdx(0);
      if (produtosFetched.length > 0) {
        setProdutoErroForm({ ...produtosFetched[0] });
      } else {
        setProdutoErroForm(null);
      }

      // Popular form de venda
      setVendaErroForm(vendaDetalhe ? { ...vendaDetalhe } : null);

      // Popular form do comprador
      setCompradorErroForm(compradorDetalhe ? {
        id: compradorDetalhe.Id ?? compradorDetalhe.id ?? null,
        vendaId: vendaId,
        tipoDocumento: compradorDetalhe.TipoDocumento ?? compradorDetalhe.tipoDocumento ?? '',
        numeroDocumento: compradorDetalhe.NumeroDocumento ?? compradorDetalhe.numeroDocumento ?? '',
        nomeRazaoSocial: compradorDetalhe.NomeRazaoSocial ?? compradorDetalhe.nomeRazaoSocial ?? '',
        logradouro: compradorDetalhe.Logradouro ?? compradorDetalhe.logradouro ?? '',
        numero: compradorDetalhe.Numero ?? compradorDetalhe.numero ?? '',
        complemento: compradorDetalhe.Complemento ?? compradorDetalhe.complemento ?? '',
        bairro: compradorDetalhe.Bairro ?? compradorDetalhe.bairro ?? '',
        codigoMunicipioIBGE: compradorDetalhe.CodigoMunicipioIBGE ?? compradorDetalhe.codigoMunicipioIBGE ?? '',
        nomeMunicipio: compradorDetalhe.NomeMunicipio ?? compradorDetalhe.nomeMunicipio ?? '',
        uf: compradorDetalhe.UF ?? compradorDetalhe.uf ?? '',
        cep: compradorDetalhe.CEP ?? compradorDetalhe.cep ?? '',
        telefone: compradorDetalhe.Telefone ?? compradorDetalhe.telefone ?? '',
        email: compradorDetalhe.Email ?? compradorDetalhe.email ?? '',
        inscricaoEstadual: compradorDetalhe.InscricaoEstadual ?? compradorDetalhe.inscricaoEstadual ?? '',
        inscricaoMunicipal: compradorDetalhe.InscricaoMunicipal ?? compradorDetalhe.inscricaoMunicipal ?? '',
        inscricaoSUFRAMA: compradorDetalhe.InscricaoSUFRAMA ?? compradorDetalhe.inscricaoSUFRAMA ?? '',
      } : null);

      // Reset aba para erro
      setAbaErroDetalhe('erro');
    } catch (err) {
      console.error('Erro ao carregar detalhe da nota com erro:', err);
      setNotaErroDetalhe({
        ...notaErro,
        mensagemErro: notaErro.mensagem || 'Erro ao carregar detalhes',
      });
    } finally {
      setDetalheErroLoading(false);
    }
  };

  // Salvar produto editado na aba Produto do detalhe de erro
  const handleSalvarProdutoErro = async () => {
    if (!produtoErroForm || !produtoErroForm.id) return;
    setSalvandoProdutoErro(true);
    try {
      await apiCall(`Produto/${produtoErroForm.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(produtoErroForm),
      });
      alert('Produto atualizado com sucesso!');
      // Atualizar lista local
      const updated = [...produtosVendaErro];
      updated[produtoSelecionadoIdx] = { ...produtoErroForm };
      setProdutosVendaErro(updated);
    } catch (err) {
      alert('Erro ao salvar produto: ' + (err.message || err));
    } finally {
      setSalvandoProdutoErro(false);
    }
  };

  // Salvar dados do comprador editado na aba Comprador do detalhe de erro
  const handleSalvarCompradorErro = async () => {
    if (!compradorErroForm) return;
    setSalvandoCompradorErro(true);
    try {
      const vendaId = notaErroDetalhe?.vendaId ?? notaErroDetalhe?.VendaId;
      if (compradorErroForm.id) {
        await apiCall(`DadosCompradorVenda/${compradorErroForm.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(compradorErroForm),
        });
      } else if (vendaId) {
        const result = await apiCall(`DadosCompradorVenda`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...compradorErroForm, vendaId }),
        });
        if (result?.id ?? result?.Id) {
          setCompradorErroForm(prev => ({ ...prev, id: result.id ?? result.Id }));
        }
      }
      alert('Dados do comprador salvos com sucesso!');
    } catch (err) {
      alert('Erro ao salvar dados do comprador: ' + (err.message || err));
    } finally {
      setSalvandoCompradorErro(false);
    }
  };

  // Gerar diagnóstico automático do erro
  const gerarDiagnosticoErro = (nota) => {
    if (!nota) return [];
    const diagnosticos = [];
    const msg = (nota.mensagemErro || '').toLowerCase();
    const xMotivo = (nota.xMotivo || '').toLowerCase();
    const erroTexto = msg + ' ' + xMotivo;

    // Análise de erros comuns da SEFAZ
    if (erroTexto.includes('rejeicao') || erroTexto.includes('rejei')) {
      diagnosticos.push({ tipo: 'rejeicao', icone: '❌', titulo: 'Nota Rejeitada pela SEFAZ', descricao: nota.xMotivo || nota.mensagemErro });
    }
    if (erroTexto.includes('ncm') || erroTexto.includes('código ncm')) {
      diagnosticos.push({ tipo: 'campo', icone: '📋', titulo: 'NCM Inválido ou Ausente', descricao: 'O código NCM de um ou mais produtos está incorreto ou não foi informado. Verifique o cadastro dos produtos.' });
    }
    if (erroTexto.includes('cfop')) {
      diagnosticos.push({ tipo: 'campo', icone: '📋', titulo: 'CFOP Inválido', descricao: 'O CFOP utilizado não é válido para esta operação. Verifique a configuração fiscal.' });
    }
    if (erroTexto.includes('cpf') || erroTexto.includes('cnpj') || erroTexto.includes('documento')) {
      diagnosticos.push({ tipo: 'campo', icone: '🪪', titulo: 'CPF/CNPJ Inválido', descricao: 'O documento do comprador está inválido, ausente ou incorreto. Verifique os dados do comprador na venda.' });
    }
    if (erroTexto.includes('endere') || erroTexto.includes('cep') || erroTexto.includes('município') || erroTexto.includes('municipio') || erroTexto.includes('ibge')) {
      diagnosticos.push({ tipo: 'campo', icone: '📍', titulo: 'Endereço Incompleto', descricao: 'Dados de endereço (CEP, município, código IBGE, UF) estão incompletos ou inválidos.' });
    }
    if (erroTexto.includes('certificado') || erroTexto.includes('assinatura')) {
      diagnosticos.push({ tipo: 'sistema', icone: '🔐', titulo: 'Erro de Certificado Digital', descricao: 'O certificado digital está expirado, inválido ou não foi encontrado. Verifique a configuração do certificado.' });
    }
    if (erroTexto.includes('timeout') || erroTexto.includes('conexão') || erroTexto.includes('conexao') || erroTexto.includes('indisponível') || erroTexto.includes('fora do ar')) {
      diagnosticos.push({ tipo: 'sistema', icone: '🌐', titulo: 'Serviço Indisponível', descricao: 'O serviço da SEFAZ está fora do ar ou houve timeout na conexão. Tente novamente mais tarde.' });
    }
    if (erroTexto.includes('duplicidade') || erroTexto.includes('duplicada') || erroTexto.includes('já autorizada')) {
      diagnosticos.push({ tipo: 'rejeicao', icone: '🔄', titulo: 'Nota Duplicada', descricao: 'Uma nota com os mesmos dados já foi autorizada. Verifique se não há duplicidade.' });
    }
    if (erroTexto.includes('ie') || erroTexto.includes('inscrição estadual') || erroTexto.includes('inscricao estadual')) {
      diagnosticos.push({ tipo: 'campo', icone: '🏢', titulo: 'Inscrição Estadual Inválida', descricao: 'A Inscrição Estadual informada está incorreta ou incompatível com o UF.' });
    }
    if (erroTexto.includes('valor') || erroTexto.includes('total') || erroTexto.includes('imposto')) {
      diagnosticos.push({ tipo: 'campo', icone: '💰', titulo: 'Valores Inconsistentes', descricao: 'Os valores totais, impostos ou descontos estão inconsistentes. Verifique os cálculos.' });
    }
    if (erroTexto.includes('schema') || erroTexto.includes('xml') || erroTexto.includes('layout')) {
      diagnosticos.push({ tipo: 'sistema', icone: '📄', titulo: 'Erro no XML da NF-e', descricao: 'O XML gerado não está de acordo com o schema esperado pela SEFAZ. Verifique os campos obrigatórios.' });
    }
    if (erroTexto.includes('failed to fetch') || erroTexto.includes('network') || erroTexto.includes('erro de conexão')) {
      diagnosticos.push({ tipo: 'sistema', icone: '📡', titulo: 'Erro de Conexão', descricao: 'Não foi possível conectar ao servidor backend. Verifique se o servidor está rodando.' });
    }
    if (erroTexto.includes('500') || erroTexto.includes('internal server') || erroTexto.includes('erro interno')) {
      diagnosticos.push({ tipo: 'sistema', icone: '⚙️', titulo: 'Erro Interno do Servidor', descricao: 'Ocorreu um erro interno no backend ao processar a nota. Verifique os logs do servidor para mais detalhes.' });
    }
    if (erroTexto.includes('dados do comprador') || erroTexto.includes('destinatário') || erroTexto.includes('destinatario')) {
      diagnosticos.push({ tipo: 'campo', icone: '👤', titulo: 'Dados do Comprador Ausentes', descricao: 'Os dados do comprador/destinatário não foram preenchidos na venda. Edite a venda e preencha os dados do comprador.' });
    }

    // Se nenhum diagnóstico específico, mostrar mensagem genérica
    if (diagnosticos.length === 0) {
      diagnosticos.push({ tipo: 'generico', icone: '⚠️', titulo: 'Erro na Emissão', descricao: nota.mensagemErro || nota.xMotivo || 'Erro desconhecido. Verifique os dados da nota e tente novamente.' });
    }

    return diagnosticos;
  };

  const handleImprimirNotas = () => {
    console.log('Imprimir notas:', notasSelecionadas);
    alert(`Imprimindo ${notasSelecionadas.length} nota(s) fiscal(is)...`);
    // TODO: Implementar integração com API
  };

  const handleExcluirNotas = () => {
    if (window.confirm(`Deseja realmente excluir ${notasSelecionadas.length} nota(s) fiscal(is)?`)) {
      console.log('Excluir notas:', notasSelecionadas);
      alert(`Excluindo ${notasSelecionadas.length} nota(s) fiscal(is)...`);
      // TODO: Implementar integração com API
    }
  };

  const handleCancelarNotas = () => {
    if (window.confirm(`Deseja realmente cancelar ${notasSelecionadas.length} nota(s) fiscal(is)?`)) {
      console.log('Cancelar notas:', notasSelecionadas);
      alert(`Cancelando ${notasSelecionadas.length} nota(s) fiscal(is)...`);
      // TODO: Implementar integração com API
    }
  };

  // Editar nota fiscal (abre popup no modo edição, apenas seleção única)
  const handleEditarNota = () => {
    if (notasSelecionadas.length !== 1) {
      alert('Selecione apenas uma nota para editar.');
      return;
    }
    const notaId = notasSelecionadas[0];
    const nota = notasAbaAtual.find(n => (n.id ?? n.Id ?? n.notaId ?? n.NotaId ?? n.numero) === notaId);
    if (nota) {
      abrirVisualizar(nota).then(() => {
        setModoEdicao(true);
      });
    }
  };

  // Baixar nota fiscal (download PDF das notas selecionadas)
  const handleBaixarNotas = async () => {
    // Abrir modal para escolha do formato antes de baixar
    const notasObj = notasSelecionadas.map(notaId => notasAbaAtual.find(n => (n.id ?? n.Id ?? n.notaId ?? n.NotaId ?? n.numero) === notaId)).filter(Boolean);
    if (notasObj.length === 0) return;
    setNotasParaBaixar(notasObj);
    setBaixarModalAberto(true);
  };

  // Fecha modal de baixar
  const handleFecharBaixarModal = () => {
    setBaixarModalAberto(false);
    setNotasParaBaixar([]);
  };

  // Baixar XML da nota fiscal
  const baixarXmlNotaFiscal = async (id) => {
    if (!id) {
      alert('ID inválido para download XML.');
      return;
    }

    const urls = [
      `${BASE_URL}/NotaFiscal/${id}/xml`,
      `${BASE_URL}/NotaFiscal/${id}/xml`,
      `${BASE_URL}/NotaFiscal/${id}/xml`,
      `${BASE_URL}/NotaFiscal/${id}/xml`,
      `${BASE_URL}/NotaFiscal/${id}/xml`
    ];

    for (const url of urls) {
      try {
        const resp = await fetch(url, { method: 'GET', headers: { 'Authorization': 'Bearer ' + (getAuthToken() || '') } });
        if (resp.ok) {
          const blob = await resp.blob();
          if (!blob || blob.size === 0) {
            alert('XML não disponível para esta Nota Fiscal.');
            return;
          }
          const dispo = resp.headers.get('Content-Disposition') || '';
          let fileName = `Nota_${id}.xml`;
          const match = /filename\*=UTF-8''(.+)$/.exec(dispo) || /filename="?([^";]+)"?/.exec(dispo);
          if (match && match[1]) fileName = decodeURIComponent(match[1]);

          const urlBlob = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = urlBlob;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(urlBlob);
          return;
        }
      } catch (error) {
        console.warn('Erro ao baixar XML em', url, error.message || error);
      }
    }

    alert('Não foi possível baixar o XML. Verifique se o backend está rodando ou se o XML existe.');
  };

  // Executa o download no formato selecionado para as notas listadas
  const executarDownloadFormato = async (formato) => {
    if (!notasParaBaixar || notasParaBaixar.length === 0) return;
    setBaixarModalAberto(false);
    for (const nota of notasParaBaixar) {
      const vendaId = nota.vendaId ?? nota.VendaId ?? nota.DadosVenda?.Id ?? nota.DadosVenda?.id ?? nota.id ?? nota.Id;
      if (!vendaId) continue;
      try {
        if (formato === 'pdf') {
          await baixarPdfNotaFiscal(vendaId);
        } else if (formato === 'xml') {
          await baixarXmlNotaFiscal(vendaId);
        }
      } catch (err) {
        console.error('Erro ao baixar nota', vendaId, err);
      }
    }
    setNotasParaBaixar([]);
    setNotasSelecionadas([]);
  };

  // Texto singular/plural para botões
  const textoNota = notasSelecionadas.length === 1 ? 'Nota' : 'Notas';

  useEffect(() => {
    fetchNotasFiscais();
  }, []);

  // Abre modal de visualização e faz GET detalhado da nota fiscal para preencher o formulário
  const abrirVisualizar = async (nota) => {
    setVisualizarLoading(true);
    setDadosVisualizar(null);
    setModoEdicao(false); // Sempre começa em modo visualização

    // tentar determinar um id válido para buscar a nota
    const notaId = nota.id || nota.Id || nota.notaId || nota.NotaId || nota.numero || nota.Numero || nota.Número || null;
    console.log('abrirVisualizar: buscando nota com id =', notaId);

    let detalheResposta = null;
    if (notaId) {
      try {
        detalheResposta = await apiCall(`NotaFiscal/${notaId}`);
      } catch (err) {
        console.error('Erro ao buscar detalhe da nota fiscal por id:', err);
      }
    }

    // Se a nota fiscal trouxer um VendaId (ou se o backend não incluir, tentar extrair do objeto),
    // buscar também a venda completa (que agora pode ter múltiplos itens) e anexar ao detalhe.
    try {
      const vendaIdFromDetalhe = detalheResposta && (detalheResposta.VendaId || detalheResposta.vendaId || (detalheResposta.Venda && (detalheResposta.Venda.Id || detalheResposta.Venda.id)));
      const vendaIdFromNota = nota && (nota.VendaId || nota.vendaId || nota.Venda || nota.venda || null);
      const vendaId = vendaIdFromDetalhe || vendaIdFromNota || null;
      if (vendaId) {
        try {
          const vendaDetalhe = await apiCall(`Vendas/${vendaId}`);
          // garantir que o objeto detalheResposta exista
          detalheResposta = detalheResposta || {};
          // anexar o detalhe da venda de maneira que as rotinas de normalização possam encontrá-lo
          detalheResposta.Venda = vendaDetalhe;
          detalheResposta.venda = vendaDetalhe;
          detalheResposta.DadosVenda = vendaDetalhe;
        } catch (err) {
          console.warn('Não foi possível carregar venda vinculada à nota (id=' + vendaId + '):', err);
        }
      }
    } catch (err) {
      console.warn('Erro ao tentar buscar venda vinculada à nota:', err);
    }

    // normalizar a resposta (prioriza detalheResposta quando disponível)
    const origem = detalheResposta && typeof detalheResposta === 'object' ? detalheResposta : nota;

    const normalizar = (d) => {
      if (!d) return {};
      // extrair dados vindos da API (várias formas possíveis)
      const Numero = d.Numero ?? d.numero ?? d.Número ?? d.Number ?? '';
  const Serie = d.Serie ?? d.serie ?? '';
      const Status = d.Status ?? d.status ?? '';
      const ChaveAcesso = d.ChaveAcesso ?? d.chaveAcesso ?? d.chave ?? '';
      const ProtocoloAutorizacao = d.ProtocoloAutorizacao ?? d.protocoloAutorizacao ?? d.Protocolo ?? '';
      const DataAutorizacao = d.DataAutorizacao ?? d.dataAutorizacao ?? d.Data_Autorizacao ?? d.DataAutorizacao ?? d.DataAutorizacao ?? null;
      const ProtocoloCancelamento = d.ProtocoloCancelamento ?? d.protocoloCancelamento ?? '';
      const DataCancelamento = d.DataCancelamento ?? d.dataCancelamento ?? d.Data_Cancelamento ?? null;
      const MotivoCancelamento = d.MotivoCancelamento ?? d.motivoCancelamento ?? d.Motivo ?? '';
      const DataEmissao = d.DataEmissao ?? d.dataEmissao ?? d.Data_Emissao ?? d.DataEmissao ?? null;
  // Prefer Valor/ValorTotal from an attached Venda (multi-item) when available
  const ValorTotal = d.DadosVenda?.ValorVenda ?? d.Venda?.ValorVenda ?? d.ValorVenda ?? d.ValorVenda ?? d.ValorTotal ?? d.valorTotal ?? d.valor ?? d.Valor ?? null;
      const Ambiente = d.Ambiente ?? d.ambiente ?? '';
      const DigestValue = d.DigestValue ?? d.digestValue ?? '';
      const CodStatusResposta = d.CodStatusResposta ?? d.codStatusResposta ?? '';
      const XMotivo = d.XMotivo ?? d.xMotivo ?? '';
  // Prefer VendaId from DadosVenda/Venda if present
  const VendaId = d.DadosVenda?.Id ?? d.DadosVenda?.id ?? d.Venda?.Id ?? d.Venda?.id ?? d.VendaId ?? d.vendaId ?? d.Venda ?? null;
      // cliente
  // Prefer cliente information from attached Venda/DadosVenda when present
  const DadosCliente = d.DadosVenda?.Cliente ?? d.DadosVenda ?? d.DadosCliente ?? d.dadosCliente ?? d.Cliente ?? d.cliente ?? null;
  const clienteId = DadosCliente?.Id ?? DadosCliente?.id ?? d.DadosVenda?.clineteId ?? d.clineteId ?? d.clienteId ?? d.ClienteId ?? null;
  const nomeCliente = DadosCliente?.Nome ?? DadosCliente?.nome ?? d.DadosVenda?.nomeCliente ?? d.NomeCliente ?? d.nomeCliente ?? '';
      const tipo = d.Tipo ?? d.tipo ?? d.TipoNota ?? d.tipoNota ?? '';

      // também anexar Items (caso exista uma venda com múltiplos itens) e o objeto bruto de DadosVenda/Venda
      const Items = d.DadosVenda?.Items ?? d.Venda?.Items ?? d.Items ?? d.items ?? null;
      const DadosVendaRaw = d.DadosVenda ?? d.Venda ?? null;

      return {
        Numero,
        Serie,
        Status,
        ChaveAcesso,
        ProtocoloAutorizacao,
        DataAutorizacao,
        ProtocoloCancelamento,
        DataCancelamento,
        MotivoCancelamento,
        DataEmissao,
        ValorTotal,
        Ambiente,
        DigestValue,
        CodStatusResposta,
        XMotivo,
        clienteId,
        nomeCliente,
        VendaId,
        valor: ValorTotal,
        tipo,
        Items,
        DadosVenda: DadosVendaRaw
      };
    };

    const normalized = normalizar(origem);
    setDadosVisualizar(normalized);

    // Buscar dados do comprador vinculados à venda
    setDadosCompradorNF({
      tipoDocumento: 'CPF',
      numeroDocumento: '',
      nomeRazaoSocial: '',
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      codigoMunicipioIBGE: '',
      nomeMunicipio: '',
      uf: '',
      cep: '',
      telefone: '',
      email: '',
      indicadorIE: '9',
      inscricaoEstadual: '',
      regimeTributario: 'SIMPLES',
      consumidorFinal: true,
      presencaComprador: '1'
    });
    setCompradorNFId(null);
    setMostrarCompradorNF(false);

    const vendaIdComp = normalized.VendaId;
    if (vendaIdComp) {
      try {
        const compResp = await apiCall(`DadosCompradorVenda/por-venda/${vendaIdComp}`);
        if (compResp) {
          setCompradorNFId(compResp.Id ?? compResp.id ?? null);
          setDadosCompradorNF({
            tipoDocumento: compResp.TipoDocumento ?? compResp.tipoDocumento ?? 'CPF',
            numeroDocumento: compResp.NumeroDocumento ?? compResp.numeroDocumento ?? '',
            nomeRazaoSocial: compResp.NomeRazaoSocial ?? compResp.nomeRazaoSocial ?? '',
            logradouro: compResp.Logradouro ?? compResp.logradouro ?? '',
            numero: compResp.Numero ?? compResp.numero ?? '',
            complemento: compResp.Complemento ?? compResp.complemento ?? '',
            bairro: compResp.Bairro ?? compResp.bairro ?? '',
            codigoMunicipioIBGE: compResp.CodigoMunicipioIBGE ?? compResp.codigoMunicipioIBGE ?? '',
            nomeMunicipio: compResp.NomeMunicipio ?? compResp.nomeMunicipio ?? '',
            uf: compResp.UF ?? compResp.uf ?? '',
            cep: compResp.CEP ?? compResp.cep ?? '',
            telefone: compResp.Telefone ?? compResp.telefone ?? '',
            email: compResp.Email ?? compResp.email ?? '',
            indicadorIE: compResp.IndicadorIE ?? compResp.indicadorIE ?? '9',
            inscricaoEstadual: compResp.InscricaoEstadual ?? compResp.inscricaoEstadual ?? '',
            regimeTributario: compResp.RegimeTributario ?? compResp.regimeTributario ?? 'SIMPLES',
            consumidorFinal: compResp.ConsumidorFinal ?? compResp.consumidorFinal ?? true,
            presencaComprador: compResp.PresencaComprador ?? compResp.presencaComprador ?? '1'
          });
          setMostrarCompradorNF(true);
        }
      } catch (err) {
        console.warn('Dados do comprador não encontrados para esta nota:', err);
      }
    }

    setVisualizarLoading(false);
    setVisualizarNF(true);
  };

  // Função para fazer chamadas da API com fallback de URLs
  const apiCall = async (endpoint, options = {}) => {
    const urls = [
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`,
      `${BASE_URL}/${endpoint}`
    ];

    const token = getAuthToken();
    if (token) {
      options = { ...options };
      options.headers = { ...options.headers, Authorization: options.headers?.Authorization || `Bearer ${token}` };
    }

    for (const url of urls) {
      try {
        console.log(`Tentando conectar em: ${url}`);
        const response = await fetch(url, options);
        if (response.ok) {
          console.log(`✅ Sucesso em: ${url}`);
          return await response.json();
        }
        console.log(`❌ Falha em: ${url} - Status: ${response.status}`);
      } catch (error) {
        console.log(`❌ Erro em: ${url} - ${error.message}`);
      }
    }
    
    throw new Error('Nenhuma URL da API funcionou. Verifique se o backend está rodando.');
  };

  // Função específica para baixar PDF da nota fiscal (retorna blob e força download)
  const baixarPdfNotaFiscal = async (id) => {
    if (!id) {
      alert('ID da nota fiscal inválido para download.');
      return;
    }

    const urls = [
      `${BASE_URL}/NotaFiscal/${id}/pdf`,
      `${BASE_URL}/NotaFiscal/${id}/pdf`,
      `${BASE_URL}/NotaFiscal/${id}/pdf`,
      `${BASE_URL}/NotaFiscal/${id}/pdf`,
      `${BASE_URL}/NotaFiscal/${id}/pdf`
    ];

    for (const url of urls) {
      try {
        console.log('Tentando baixar PDF em:', url);
        const resp = await fetch(url, { method: 'GET', headers: { 'Authorization': 'Bearer ' + (getAuthToken() || '') } });
        if (resp.ok) {
          const blob = await resp.blob();
          if (!blob || blob.size === 0) {
            alert('PDF não disponível para esta Nota Fiscal.');
            return;
          }
          // Tentar extrair filename do header
          const dispo = resp.headers.get('Content-Disposition') || '';
          let fileName = `NFe_${id}.pdf`;
          const match = /filename\*=UTF-8''(.+)$/.exec(dispo) || /filename="?([^";]+)"?/.exec(dispo);
          if (match && match[1]) fileName = decodeURIComponent(match[1]);

          const urlBlob = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = urlBlob;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(urlBlob);
          return;
        } else {
          // se 404, tentar próxima URL
          console.warn('Falha ao baixar PDF (status):', resp.status, url);
          // se for 404 especificamente, continue para tentar outras URLs
        }
      } catch (error) {
        console.warn('Erro ao baixar PDF em', url, error.message || error);
      }
    }

    alert('Não foi possível baixar o PDF. Verifique se o backend está rodando ou se o PDF existe.');
  };

  const fetchNotasFiscais = async () => {
    try {
      setLoading(true);
      const data = await apiCall('NotaFiscal');
      const lista = Array.isArray(data) ? data : [];
      setError('');

      // Enriquecer: utilizar os campos já retornados pelo GET geral (DadosCliente / DadosVenda)
      const notasPromises = (lista || []).map(async (nota) => {
  const notaId = nota.id ?? nota.Id ?? nota.numero ?? nota.Numero ?? nota.Numero ?? null;
        // aproveitar campos já incluídos no GET geral pelo backend
        const dadosCliente = nota.DadosCliente ?? nota.dadosCliente ?? nota.Cliente ?? nota.cliente ?? null;
        const dadosVenda = nota.DadosVenda ?? nota.dadosVenda ?? nota.Venda ?? nota.venda ?? null;

        // tentar extrair clienteId e nomeCliente das estruturas já retornadas
        const clienteId = dadosCliente?.Id ?? dadosCliente?.id ?? dadosCliente?.ClienteId ?? dadosCliente?.clienteId ?? dadosVenda?.Id ?? dadosVenda?.ClineteId ?? nota.clienteId ?? nota.ClienteId ?? nota.clienteId ?? '';
        const nomeCliente = dadosCliente?.Nome ?? dadosCliente?.nome ?? dadosCliente?.RazaoSocial ?? dadosVenda?.NomeCliente ?? dadosVenda?.NomeCliente ?? nota.NomeCliente ?? nota.nomeCliente ?? nota.nome ?? '';

        // normalizar tipo
        let tipoVal = nota.tipo ?? nota.Tipo ?? nota.TipoNota ?? nota.tipoNota ?? null;
        if (tipoVal && typeof tipoVal === 'object') {
          tipoVal = tipoVal.descricao ?? tipoVal.Descricao ?? tipoVal.nome ?? tipoVal.Nome ?? tipoVal.tipo ?? JSON.stringify(tipoVal);
        }

        const valor = nota.valor ?? nota.Valor ?? nota.valorTotal ?? nota.ValorTotal ?? nota.Valor_Total ?? nota.ValorFiscal ?? null;

        // Extrair tipoVenda
        const tipoVenda = nota.tipoVenda ?? nota.TipoVenda ?? nota.tipo_venda ?? dadosVenda?.TipoVendaNatureza ?? dadosVenda?.tipoVendaNatureza ?? dadosVenda?.tipo_venda ?? null;
        const tipoDocumento = nota.tipoDocumento ?? nota.TipoDocumento ?? null;

        return {
          ...nota,
          id: notaId ?? (nota.id ?? nota.Id) ,
          clienteId: clienteId || '',
          nomeCliente: nomeCliente || '',
          valor,
          tipo: tipoVal,
          tipoVenda: tipoVenda,
          tipoDocumento: tipoDocumento
        };
      });

      const notasCompletas = await Promise.all(notasPromises);
      setNotasFiscais(notasCompletas);
      setNotasFiltradas(notasCompletas);
    } catch (err) {
      console.error('Erro ao buscar notas fiscais:', err);
      setError('Erro ao carregar notas fiscais. Verifique se o backend está rodando.');
      setNotasFiscais([]);
      setNotasFiltradas([]);
    } finally {
      setLoading(false);
    }
  };

  // Funções de filtro
  const handleFiltroChange = (novoFiltro) => {
    setFiltro(novoFiltro);
    setDropdownAberto(false);
    setValorFiltro('');
    // Resetar para todas as notas quando muda o filtro
    setNotasFiltradas(notasFiscais);
  };

  const handleInputChange = (e) => {
    const valor = e.target.value;
    setValorFiltro(valor);
    aplicarFiltro(valor);
  };

  const aplicarFiltro = (valor) => {
    if (!valor.trim()) {
      setNotasFiltradas(notasFiscais);
      return;
    }

    const filtradas = notasFiscais.filter(nota => {
      if (filtro === 'numero') {
        const num = obter(nota, 'numero', 'Numero', 'NumeroNota');
        return num && String(num).includes(valor);
      }
      if (filtro === 'clienteId') {
        const cid = obter(nota, 'clienteId', 'ClienteId', 'Id_cliente', 'clienteID');
        return cid && String(cid).includes(valor);
      }
      if (filtro === 'clienteNome') {
        const nome = obter(nota, 'nomeCliente', 'NomeCliente', 'nome', 'Nome');
        return nome && String(nome).toLowerCase().includes(valor.toLowerCase());
      }
      if (filtro === 'valor') {
        const v = obter(nota, 'valor', 'Valor', 'valorTotal', 'ValorTotal');
        return v && String(v).includes(valor);
      }
      if (filtro === 'status') {
        const s = obter(nota, 'status', 'Status');
        return s && String(s).toLowerCase().includes(valor.toLowerCase());
      }
      return false;
    });

    setNotasFiltradas(filtradas);
  };

  const limparFiltro = () => {
    setValorFiltro('');
    setNotasFiltradas(notasFiscais);
  };

  return (
    <div className="container-geral fundo-branco" style={{ 
      minHeight: '100vh', 
      background: '#f0f0f0', 
      padding: '20px'
    }}>
      
      {/* Filtro de notas fiscais */}
      <div style={{
        width: '100%',
        maxWidth: 1200,
        margin: '0 auto 24px auto',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        padding: '0 20px',
        justifyContent: 'center'
      }}>
        <label style={{ 
          color: '#333', 
          fontSize: '16px', 
          fontWeight: '500',
          whiteSpace: 'nowrap'
        }}>
          Filtrar por:
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <input
              type="text"
              style={{
                padding: '10px 15px',
                paddingRight: valorFiltro ? '40px' : '15px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                fontSize: '14px',
                minWidth: '250px',
                outline: 'none'
              }}
              placeholder={`Filtrar por ${opcoesFiltro.find(opcao => opcao.valor === filtro)?.label || 'campo'}`}
              value={valorFiltro}
              onChange={handleInputChange}
            />
            {valorFiltro && (
              <button
                onClick={limparFiltro}
                title="Limpar filtro"
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  color: 'rgb(235, 133, 0)',
                  border: 'none',
                  width: '20px',
                  height: '20px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  lineHeight: '1',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  outline: 'none',
                  boxShadow: 'none'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'rgb(235, 133, 0)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'rgb(235, 133, 0)';
                }}
                onFocus={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'rgb(235, 133, 0)';
                  e.target.style.outline = 'none';
                }}
              >
                ×
              </button>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setDropdownAberto(!dropdownAberto)}
              style={{
                padding: '10px 15px',
                backgroundColor: 'rgb(235, 133, 0)',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minWidth: '200px',
                justifyContent: 'space-between'
              }}
            >
              {opcoesFiltro.find(opcao => opcao.valor === filtro)?.label || 'Selecione'}
              <span style={{ transform: dropdownAberto ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
            </button>
            {dropdownAberto && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'rgb(235, 133, 0)',
                border: '1px solid rgb(235, 133, 0)',
                borderRadius: '5px',
                marginTop: '2px',
                zIndex: 1000,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                {opcoesFiltro.map(opcao => (
                  <div 
                    key={opcao.valor}
                    onClick={() => handleFiltroChange(opcao.valor)}
                    style={{ 
                      padding: '10px 15px', 
                      cursor: 'pointer',
                      borderBottom: '1px solid rgba(255,255,255,0.2)',
                      color: 'white'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    {opcao.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Título centralizado */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '32px' 
      }}>
        <h1 style={{ 
          color: 'rgb(235, 133, 0)', 
          fontSize: '32px', 
          fontWeight: 'bold',
          margin: '0 0 16px 0' 
        }}>
          Notas Fiscais
        </h1>

        {/* Toggle Notas de Produtos / Notas de Serviços */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
          <button
            onClick={() => handleTrocarTipoNota('produtos')}
            style={{
              padding: '10px 28px',
              fontSize: '15px',
              fontWeight: '600',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              backgroundColor: tipoNotaFiscal === 'produtos' ? '#fff' : '#e0e0e0',
              color: tipoNotaFiscal === 'produtos' ? 'rgb(235, 133, 0)' : '#333',
              boxShadow: tipoNotaFiscal === 'produtos' ? '0 0 0 2px rgba(235, 133, 0,0.25)' : 'none'
            }}
          >
            Notas de Produtos
          </button>
          <button
            onClick={() => handleTrocarTipoNota('servicos')}
            style={{
              padding: '10px 28px',
              fontSize: '15px',
              fontWeight: '600',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              backgroundColor: tipoNotaFiscal === 'servicos' ? '#fff' : '#e0e0e0',
              color: tipoNotaFiscal === 'servicos' ? 'rgb(235, 133, 0)' : '#333',
              boxShadow: tipoNotaFiscal === 'servicos' ? '0 0 0 2px rgba(235, 133, 0,0.25)' : 'none'
            }}
          >
            Notas de Serviços
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#666',
          backgroundColor: 'white',
          borderRadius: '8px',
          margin: '0 auto',
          maxWidth: 1200
        }}>
          Carregando notas fiscais...
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#d32f2f',
          margin: '0 auto',
          maxWidth: 1200
        }}>
          {error}
        </div>
      )}

      {/* Container principal com abas */}
      {!loading && !error && (
        <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: notasSelecionadas.length > 0 ? '100px' : '20px' }}>
          
          {/* Abas */}
          <div style={{
            display: 'flex',
            gap: '0',
            borderBottom: '1px solid #333',
            marginBottom: '0'
          }}>
            {/* Aba Pendentes */}
            <button
              onClick={() => handleTrocarAba('pendentes')}
              style={{
                padding: '12px 24px',
                backgroundColor: 'transparent',
                borderRadius: 0,
                border: 'none',
                borderBottom: abaAtiva === 'pendentes' ? '3px solid rgb(235, 133, 0)' : '3px solid transparent',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: abaAtiva === 'pendentes' ? '600' : '400',
                color: abaAtiva === 'pendentes' ? '#333' : '#666',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
            >
              <span style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: '#ffc107',
                display: 'inline-block'
              }}></span>
              Pendentes ({notasPendentes.length})
            </button>

            {/* Aba Emitidas/Concluídas */}
            <button
              onClick={() => handleTrocarAba('concluidas')}
              style={{
                padding: '12px 24px',
                backgroundColor: 'transparent',
                borderRadius: 0,
                border: 'none',
                borderBottom: abaAtiva === 'concluidas' ? '3px solid rgb(235, 133, 0)' : '3px solid transparent',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: abaAtiva === 'concluidas' ? '600' : '400',
                color: abaAtiva === 'concluidas' ? '#333' : '#666',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
            >
              <span style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: '#4caf50',
                display: 'inline-block'
              }}></span>
              Emitidas ({notasConcluidas.length})
            </button>
          </div>

          {/* Conteúdo da aba */}
          {notasAbaAtual.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#666'
            }}>
              {valorFiltro 
                ? 'Nenhuma nota fiscal encontrada com este filtro.' 
                : abaAtiva === 'pendentes' 
                  ? 'Nenhuma nota fiscal pendente.' 
                  : 'Nenhuma nota fiscal emitida.'}
            </div>
          ) : (
            <>
              {/* Checkbox Todos */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 0',
                borderBottom: '1px solid #333'
              }}>
                <div
                  onClick={toggleSelecionarTodas}
                  style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '4px',
                    border: todasSelecionadas ? 'none' : '2px solid #ccc',
                    backgroundColor: todasSelecionadas ? '#e67600' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {todasSelecionadas && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12l5 5L19 7" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <span style={{ fontSize: '15px', fontWeight: '500', color: '#333' }}>Todos</span>
              </div>

              {/* Cards das Notas */}
              {notasAbaAtual.map((nota) => {
                const notaKey = nota.id ?? nota.Id ?? nota.notaId ?? Math.random().toString(36).slice(2,9);
                const idNota = nota.id ?? nota.Id ?? nota.notaId ?? nota.NotaId ?? nota.numero ?? '';
                const numero = nota.numero ?? nota.Numero ?? nota.NumeroNota ?? '';
                const nomeCliente = nota.nomeCliente ?? nota.NomeCliente ?? nota.nome ?? nota.Nome ?? obterAninhado(nota, 'cliente.nome', 'cliente.Nome') ?? '';
                const valorCampo = nota.valor ?? nota.Valor ?? nota.valorTotal ?? nota.ValorTotal ?? null;
                const status = nota.status ?? nota.Status ?? '';
                const selecionada = isNotaSelecionada(idNota);

                return (
                  <div 
                    key={notaKey} 
                    onClick={() => abrirVisualizar(nota)}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px 0',
                      backgroundColor: selecionada ? '#fff3e0' : 'transparent',
                      borderBottom: '1px solid #333',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!selecionada) e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.02)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = selecionada ? '#fff3e0' : 'transparent';
                    }}
                  >
                    {/* Checkbox */}
                    <div
                      onClick={(e) => toggleSelecaoNota(idNota, e)}
                      style={{
                        width: '22px',
                        height: '22px',
                        borderRadius: '4px',
                        border: selecionada ? 'none' : '2px solid #ccc',
                        backgroundColor: selecionada ? '#e67600' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        flexShrink: 0,
                        transition: 'all 0.2s'
                      }}
                    >
                      {selecionada && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M5 12l5 5L19 7" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>

                    {/* Dados da Nota */}
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '30px', flex: 1, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: '100px' }}>
                        <label style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '13px', color: '#666' }}>Número</label>
                        <span style={{ fontSize: '14px', color: '#333' }}>{numero ?? ''}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: '80px' }}>
                        <label style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '13px', color: '#666' }}>ID</label>
                        <span style={{ fontSize: '14px', color: '#333' }}>{idNota ?? ''}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: '150px' }}>
                        <label style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '13px', color: '#666' }}>Nome Cliente</label>
                        <span style={{ fontSize: '14px', color: '#333' }}>{nomeCliente}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: '120px' }}>
                        <label style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '13px', color: '#666' }}>Valor (R$)</label>
                        <span style={{ fontSize: '14px', color: '#333' }}>
                          {valorCampo != null ? `R$ ${Number(valorCampo).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: '100px' }}>
                        <label style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '13px', color: '#666' }}>Status</label>
                        <span style={{ 
                          fontSize: '14px', 
                          fontWeight: '500',
                          color: status?.toLowerCase().includes('emitida') ? '#4caf50' : status?.toLowerCase().includes('cancelada') ? '#f44336' : 'rgb(235, 133, 0)' 
                        }}>
                          {status || '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* Footer Fixo com Ações - Aba Pendentes - Notas de Produtos */}
      {notasSelecionadas.length > 0 && abaAtiva === 'pendentes' && tipoNotaFiscal === 'produtos' && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'white',
          borderTop: '2px solid #e0e0e0',
          padding: '20px 24px',
          minHeight: '72px',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '16px',
          zIndex: 9998,
          boxShadow: '0 -4px 12px rgba(0,0,0,0.1)'
        }}>
          {/* Botão Autorizar */}
          <button
            onClick={handleAutorizarNotas}
            style={{
              padding: '12px 24px',
              backgroundColor: 'rgb(235, 133, 0)',
              color: 'white',
              border: 'none',
              borderRadius: '30px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(208, 116, 0)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(235, 133, 0)'}
          >
            Autorizar {textoNota}
          </button>

          {/* Botão Baixar Nota (laranja) - Produtos também podem baixar */}
          <button
            onClick={handleBaixarNotas}
            style={{
              padding: '12px 24px',
              backgroundColor: 'rgb(235, 133, 0)',
              color: 'white',
              border: 'none',
              borderRadius: '30px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(208, 116, 0)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(235, 133, 0)'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="7 10 12 15 17 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="12" y1="15" x2="12" y2="3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Baixar {textoNota}
          </button>

          {/* Botão Editar Nota (apenas seleção única) */}
          {notasSelecionadas.length === 1 && (
            <button
              onClick={handleEditarNota}
              style={{
                padding: '12px 24px',
                backgroundColor: '#757575',
                color: 'white',
                border: 'none',
                borderRadius: '30px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#616161'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#757575'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Editar Nota
            </button>
          )}

          {/* Botão Imprimir */}
          <button
            onClick={handleImprimirNotas}
            style={{
              padding: '12px 24px',
              backgroundColor: '#757575',
              color: 'white',
              border: 'none',
              borderRadius: '30px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#616161'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#757575'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M6 9V2h12v7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <rect x="6" y="14" width="12" height="8" stroke="white" strokeWidth="2"/>
            </svg>
            Imprimir {textoNota}
          </button>

          {/* Botão Excluir */}
          <button
            onClick={handleExcluirNotas}
            style={{
              padding: '12px 24px',
              backgroundColor: '#757575',
              color: 'white',
              border: 'none',
              borderRadius: '30px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#616161'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#757575'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M3 6h18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="10" y1="11" x2="10" y2="17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="14" y1="11" x2="14" y2="17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Excluir
          </button>
          {/* Contador */}
          <span style={{ color: '#666', fontSize: '14px', marginLeft: '12px' }}>
            {notasSelecionadas.length} {textoNota.toLowerCase()} selecionada{notasSelecionadas.length > 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Footer Fixo com Ações - Aba Pendentes - Notas de Serviço */}
      {notasSelecionadas.length > 0 && abaAtiva === 'pendentes' && tipoNotaFiscal === 'servicos' && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'white',
          borderTop: '2px solid #e0e0e0',
          padding: '20px 24px',
          minHeight: '72px',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '16px',
          zIndex: 9998,
          boxShadow: '0 -4px 12px rgba(0,0,0,0.1)'
        }}>
          {/* Botão Baixar Nota (laranja) */}
          <button
            onClick={handleBaixarNotas}
            style={{
              padding: '12px 24px',
              backgroundColor: 'rgb(235, 133, 0)',
              color: 'white',
              border: 'none',
              borderRadius: '30px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(208, 116, 0)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(235, 133, 0)'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="7 10 12 15 17 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="12" y1="15" x2="12" y2="3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Baixar {textoNota}
          </button>

          {/* Botão Editar Nota (apenas seleção única) */}
          {notasSelecionadas.length === 1 && (
            <button
              onClick={handleEditarNota}
              style={{
                padding: '12px 24px',
                backgroundColor: '#757575',
                color: 'white',
                border: 'none',
                borderRadius: '30px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#616161'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#757575'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Editar Nota
            </button>
          )}

          {/* Botão Imprimir */}
          <button
            onClick={handleImprimirNotas}
            style={{
              padding: '12px 24px',
              backgroundColor: '#757575',
              color: 'white',
              border: 'none',
              borderRadius: '30px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#616161'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#757575'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M6 9V2h12v7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <rect x="6" y="14" width="12" height="8" stroke="white" strokeWidth="2"/>
            </svg>
            Imprimir {textoNota}
          </button>

          {/* Botão Excluir */}
          <button
            onClick={handleExcluirNotas}
            style={{
              padding: '12px 24px',
              backgroundColor: '#757575',
              color: 'white',
              border: 'none',
              borderRadius: '30px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#616161'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#757575'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M3 6h18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="10" y1="11" x2="10" y2="17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="14" y1="11" x2="14" y2="17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Excluir
          </button>
          {/* Contador */}
          <span style={{ color: '#666', fontSize: '14px', marginLeft: '12px' }}>
            {notasSelecionadas.length} {textoNota.toLowerCase()} selecionada{notasSelecionadas.length > 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Footer Fixo com Ações - Aba Concluídas (só Cancelar) */}
      {notasSelecionadas.length > 0 && abaAtiva === 'concluidas' && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'white',
          borderTop: '2px solid #e0e0e0',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px',
          zIndex: 9998,
          boxShadow: '0 -4px 12px rgba(0,0,0,0.1)'
        }}>
          <span style={{ color: '#666', fontSize: '14px', marginRight: '8px' }}>
            {notasSelecionadas.length} {textoNota.toLowerCase()} selecionada{notasSelecionadas.length > 1 ? 's' : ''}
          </span>
          
          {/* Botão Cancelar Nota */}
          <button
            onClick={handleCancelarNotas}
            style={{
              padding: '12px 24px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '30px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2"/>
              <path d="M15 9l-6 6M9 9l6 6" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Cancelar {textoNota}
          </button>
        </div>
      )}

      {/* Modal de visualização/edição de nota fiscal */}
      {visualizarNF && (
        <div className="modal-overlay" onClick={() => { setVisualizarNF(false); setModoEdicao(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000 }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: '90%', maxWidth: 900, background: '#fff', borderRadius: 12, padding: 24, maxHeight: '85vh', overflowY: 'auto', zIndex: 100001 }}>
            {/* Header do Modal */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, borderBottom: '2px solid #f0f0f0', paddingBottom: 16 }}>
              <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)', fontSize: '20px' }}>
                Nota Fiscal - {modoEdicao ? 'Editar' : 'Visualizar'}
              </h3>
              <button
                onClick={() => { setVisualizarNF(false); setModoEdicao(false); }}
                aria-label="Fechar"
                title="Fechar"
                style={{
                  backgroundColor: 'rgb(235, 133, 0)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                }}
              >
                ×
              </button>
            </div>

            {visualizarLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Carregando dados da nota fiscal...</div>
            ) : (
              <form onSubmit={e => { e.preventDefault(); if (modoEdicao) { alert('Salvando alterações...'); /* TODO: Implementar PUT */ } }} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* Campos da Nota Fiscal */}
                {[
                  { id: 'Numero', label: 'Número' },
                  { id: 'Serie', label: 'Série' },
                  { id: 'Status', label: 'Status' },
                  { id: 'ChaveAcesso', label: 'Chave de Acesso' },
                  { id: 'ProtocoloAutorizacao', label: 'Protocolo Autorização' },
                  { id: 'DataAutorizacao', label: 'Data Autorização', type: 'date' },
                  { id: 'ProtocoloCancelamento', label: 'Protocolo Cancelamento' },
                  { id: 'DataCancelamento', label: 'Data Cancelamento', type: 'date' },
                  { id: 'MotivoCancelamento', label: 'Motivo Cancelamento' },
                  { id: 'DataEmissao', label: 'Data Emissão', type: 'date' },
                  { id: 'ValorTotal', label: 'Valor Total (R$)', type: 'money' },
                  { id: 'Ambiente', label: 'Ambiente' },
                  { id: 'DigestValue', label: 'Digest Value' },
                  { id: 'CodStatusResposta', label: 'Código Status Resposta' },
                  { id: 'XMotivo', label: 'XMotivo' },
                  { id: 'clienteId', label: 'ID Cliente' },
                  { id: 'nomeCliente', label: 'Nome Cliente' },
                  { id: 'VendaId', label: 'Venda ID' },
                  { id: 'PdfDisponivel', label: 'PDF', type: 'pdf' },
                  { id: 'valor', label: 'Valor (Total) (R$)', type: 'money' },
                  { id: 'tipo', label: 'Tipo' }
                ].map(field => {
                  const rawVal = dadosVisualizar?.[field.id] ?? dadosVisualizar?.[field.id.charAt(0).toUpperCase() + field.id.slice(1)] ?? '';
                  const isMoney = field.type === 'money' || /valor|Valor|ValorTotal|valorTotal|ValorTotal/.test(field.id);
                  return (
                    <div key={field.id} style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: 13, marginBottom: 6, fontWeight: '500', color: '#555' }}>{field.label}</label>
                      {field.type === 'pdf' ? (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <input 
                            type="text" 
                            value={dadosVisualizar?.VendaId ?? dadosVisualizar?.vendaId ?? dadosVisualizar?.venda?.Id ?? ''} 
                            readOnly 
                            style={{ 
                              padding: '10px', 
                              borderRadius: 6, 
                              border: '1px solid #ddd', 
                              width: '120px',
                              backgroundColor: '#f5f5f5'
                            }} 
                          />
                          <button 
                            type="button" 
                            onClick={() => baixarPdfNotaFiscal(dadosVisualizar?.VendaId ?? dadosVisualizar?.vendaId ?? dadosVisualizar?.venda?.Id ?? dadosVisualizar?.VendaId)} 
                            style={{ background: '#1976d2', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: '500' }}
                          >
                            Baixar PDF
                          </button>
                        </div>
                      ) : isMoney ? (
                        <input
                          type="text"
                          placeholder={formatBRL('') || 'R$ 0,00'}
                          value={rawVal !== '' && rawVal !== null ? formatBRL(rawVal) : ''}
                          readOnly={!modoEdicao}
                          onChange={e => {
                            if (modoEdicao) {
                              const parsed = parseBRLToNumber(e.target.value);
                              setDadosVisualizar({ ...dadosVisualizar, [field.id]: parsed });
                            }
                          }}
                          onBlur={e => {
                            if (modoEdicao) {
                              const parsed = parseBRLToNumber(e.target.value);
                              setDadosVisualizar({ ...dadosVisualizar, [field.id]: parsed });
                            }
                          }}
                          style={{ 
                            padding: '10px', 
                            borderRadius: 6, 
                            border: '1px solid #ddd',
                            backgroundColor: modoEdicao ? 'white' : '#f5f5f5',
                            cursor: modoEdicao ? 'text' : 'default'
                          }}
                        />
                      ) : (
                        <input
                          type={field.type || 'text'}
                          value={rawVal}
                          readOnly={!modoEdicao}
                          onChange={e => {
                            if (modoEdicao) {
                              setDadosVisualizar({ ...dadosVisualizar, [field.id]: e.target.value });
                            }
                          }}
                          style={{ 
                            padding: '10px', 
                            borderRadius: 6, 
                            border: '1px solid #ddd',
                            backgroundColor: modoEdicao ? 'white' : '#f5f5f5',
                            cursor: modoEdicao ? 'text' : 'default'
                          }}
                        />
                      )}
                    </div>
                  );
                })}

                {/* Itens da Venda */}
                {(dadosVisualizar?.Items ?? dadosVisualizar?.DadosVenda?.Items ?? dadosVisualizar?.venda?.Items) && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <h4 style={{ marginTop: 10, marginBottom: 10, color: '#333' }}>Itens da Venda</h4>
                    <div style={{ border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden' }}>
                      <div style={{ display: 'flex', background: '#f5f5f5', padding: '10px 14px', fontWeight: 600 }}>
                        <div style={{ flex: 3 }}>Produto</div>
                        <div style={{ flex: 1, textAlign: 'right' }}>Quantidade</div>
                        <div style={{ flex: 1, textAlign: 'right' }}>Valor Unit.</div>
                        <div style={{ flex: 1, textAlign: 'right' }}>Valor Total</div>
                      </div>
                      {(dadosVisualizar?.Items ?? dadosVisualizar?.DadosVenda?.Items ?? dadosVisualizar?.venda?.Items).map(item => (
                        <div key={item.Id ?? item.id ?? item.ProdutoId ?? JSON.stringify(item)} style={{ display: 'flex', padding: '10px 14px', borderTop: '1px solid #eee', alignItems: 'center' }}>
                          <div style={{ flex: 3 }}>{item.NomeProduto ?? item.nomeProduto ?? item.nome ?? item.ProdutoNome ?? ''}</div>
                          <div style={{ flex: 1, textAlign: 'right' }}>{item.Quantidade ?? item.quantidade ?? item.Qtd ?? ''}</div>
                          <div style={{ flex: 1, textAlign: 'right' }}>{formatBRL(item.ValorUnitario ?? item.valorUnitario ?? item.ValorUnit ?? 0)}</div>
                          <div style={{ flex: 1, textAlign: 'right' }}>{formatBRL(item.ValorTotal ?? item.valorTotal ?? item.total ?? ((item.Quantidade ?? item.quantidade ?? 0) * (item.ValorUnitario ?? item.valorUnitario ?? 0)))}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ===== DADOS DO COMPRADOR ===== */}
                {dadosVisualizar?.VendaId && (
                  <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
                    <div
                      style={{ padding: '14px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: mostrarCompradorNF ? '#1565c0' : '#1976d2', color: 'white', userSelect: 'none', borderRadius: mostrarCompradorNF ? '10px 10px 0 0' : '10px' }}
                      onClick={() => setMostrarCompradorNF(p => !p)}
                    >
                      <span style={{ fontWeight: 'bold' }}>📋 Dados do Comprador (NF-e)</span>
                      <span style={{ fontSize: '18px' }}>{mostrarCompradorNF ? '▲' : '▼'}</span>
                    </div>
                    {!mostrarCompradorNF && (
                      <p style={{ fontSize: '13px', color: '#777', margin: '6px 0 0 0' }}>
                        Clique para expandir e editar os dados do comprador.{' '}
                        <span style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setMostrarCompradorNF(true)}>Expandir</span>
                      </p>
                    )}
                    {mostrarCompradorNF && dadosCompradorNF && (
                      <div style={{ padding: '20px', background: '#f0f7ff', borderRadius: '0 0 10px 10px' }}>
                        <p style={{ color: 'rgb(184, 98, 0)', fontSize: '13px', background: '#fff3e0', padding: '10px 14px', borderRadius: '6px', margin: '0 0 18px 0' }}>
                          ⚠️ Campos com <strong>*</strong> são necessários para emissão da NF-e. Você pode salvar a qualquer momento.
                        </p>

                        {/* Tipo + Documento + Nome */}
                        <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                          <div>
                            <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px', color: '#333' }}>Tipo *</label>
                            <select value={dadosCompradorNF.tipoDocumento}
                              onChange={e => setDadosCompradorNF(p => ({ ...p, tipoDocumento: e.target.value, numeroDocumento: '' }))}
                              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px' }}>
                              <option value="CPF">CPF (Pessoa Física)</option>
                              <option value="CNPJ">CNPJ (Pessoa Jurídica)</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px', color: '#333' }}>
                              {dadosCompradorNF.tipoDocumento === 'CNPJ' ? 'CNPJ *' : 'CPF *'}
                            </label>
                            <input type="text" value={dadosCompradorNF.numeroDocumento}
                              onChange={e => setDadosCompradorNF(p => ({ ...p, numeroDocumento: e.target.value.replace(/\D/g, '') }))}
                              maxLength={dadosCompradorNF.tipoDocumento === 'CNPJ' ? 14 : 11}
                              placeholder={dadosCompradorNF.tipoDocumento === 'CNPJ' ? '14 dígitos' : '11 dígitos'}
                              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px', color: '#333' }}>
                              {dadosCompradorNF.tipoDocumento === 'CNPJ' ? 'Razão Social *' : 'Nome Completo *'}
                            </label>
                            <input type="text" value={dadosCompradorNF.nomeRazaoSocial}
                              onChange={e => setDadosCompradorNF(p => ({ ...p, nomeRazaoSocial: e.target.value }))}
                              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', boxSizing: 'border-box' }} />
                          </div>
                        </div>

                        {/* Logradouro + Número + Complemento */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 1fr', gap: '14px', marginBottom: '14px' }}>
                          <div>
                            <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px', color: '#333' }}>Logradouro *</label>
                            <input type="text" value={dadosCompradorNF.logradouro}
                              onChange={e => setDadosCompradorNF(p => ({ ...p, logradouro: e.target.value }))}
                              placeholder="Rua, Av.…"
                              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px', color: '#333' }}>Número *</label>
                            <input type="text" value={dadosCompradorNF.numero}
                              onChange={e => setDadosCompradorNF(p => ({ ...p, numero: e.target.value }))}
                              placeholder="S/N"
                              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px', color: '#333' }}>Complemento</label>
                            <input type="text" value={dadosCompradorNF.complemento}
                              onChange={e => setDadosCompradorNF(p => ({ ...p, complemento: e.target.value }))}
                              placeholder="Apto, sala…"
                              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', boxSizing: 'border-box' }} />
                          </div>
                        </div>

                        {/* Bairro + Município + UF + CEP */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px 120px', gap: '14px', marginBottom: '14px' }}>
                          <div>
                            <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px', color: '#333' }}>Bairro *</label>
                            <input type="text" value={dadosCompradorNF.bairro}
                              onChange={e => setDadosCompradorNF(p => ({ ...p, bairro: e.target.value }))}
                              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px', color: '#333' }}>Município *</label>
                            <input type="text" value={dadosCompradorNF.nomeMunicipio}
                              onChange={e => setDadosCompradorNF(p => ({ ...p, nomeMunicipio: e.target.value }))}
                              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px', color: '#333' }}>UF *</label>
                            <select value={dadosCompradorNF.uf}
                              onChange={e => setDadosCompradorNF(p => ({ ...p, uf: e.target.value }))}
                              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px' }}>
                              <option value="">--</option>
                              {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf => (
                                <option key={uf} value={uf}>{uf}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px', color: '#333' }}>CEP *</label>
                            <input type="text" value={(dadosCompradorNF.cep || '').replace(/^(\d{5})(\d)/, '$1-$2')}
                              onChange={e => setDadosCompradorNF(p => ({ ...p, cep: e.target.value.replace(/\D/g, '').slice(0, 8) }))}
                              maxLength={10} placeholder="00000-000"
                              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', boxSizing: 'border-box' }} />
                          </div>
                        </div>

                        {/* Cód. IBGE + Telefone + E-mail */}
                        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                          <div>
                            <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px', color: '#333' }}>Cód. IBGE *</label>
                            <input type="text" value={dadosCompradorNF.codigoMunicipioIBGE}
                              onChange={e => setDadosCompradorNF(p => ({ ...p, codigoMunicipioIBGE: e.target.value.replace(/\D/g, '') }))}
                              maxLength={7} placeholder="7 dígitos"
                              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px', color: '#333' }}>Telefone</label>
                            <input type="text" value={dadosCompradorNF.telefone}
                              onChange={e => setDadosCompradorNF(p => ({ ...p, telefone: e.target.value }))}
                              placeholder="(11) 99999-9999"
                              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px', color: '#333' }}>E-mail</label>
                            <input type="email" value={dadosCompradorNF.email}
                              onChange={e => setDadosCompradorNF(p => ({ ...p, email: e.target.value }))}
                              placeholder="comprador@email.com"
                              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', boxSizing: 'border-box' }} />
                          </div>
                        </div>

                        {/* Dados fiscais do comprador */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                          <div>
                            <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px', color: '#333' }}>Indicador IE</label>
                            <select value={dadosCompradorNF.indicadorIE}
                              onChange={e => setDadosCompradorNF(p => ({ ...p, indicadorIE: e.target.value }))}
                              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px' }}>
                              <option value="9">9 – Não contribuinte</option>
                              <option value="1">1 – Contribuinte ICMS</option>
                              <option value="2">2 – Isento de IE</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px', color: '#333' }}>Inscrição Estadual</label>
                            <input type="text" value={dadosCompradorNF.inscricaoEstadual}
                              disabled={dadosCompradorNF.indicadorIE !== '1'}
                              onChange={e => setDadosCompradorNF(p => ({ ...p, inscricaoEstadual: e.target.value }))}
                              placeholder={dadosCompradorNF.indicadorIE !== '1' ? 'N/A' : 'Número da IE'}
                              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px', boxSizing: 'border-box', background: dadosCompradorNF.indicadorIE !== '1' ? '#f5f5f5' : 'white' }} />
                          </div>
                          <div>
                            <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px', color: '#333' }}>Regime Tributário</label>
                            <select value={dadosCompradorNF.regimeTributario}
                              onChange={e => setDadosCompradorNF(p => ({ ...p, regimeTributario: e.target.value }))}
                              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px' }}>
                              <option value="SIMPLES">Simples Nacional</option>
                              <option value="MEI">MEI</option>
                              <option value="PRESUMIDO">Lucro Presumido</option>
                              <option value="REAL">Lucro Real</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ fontWeight: 'bold', fontSize: '13px', display: 'block', marginBottom: '6px', color: '#333' }}>Presença do Comprador</label>
                            <select value={dadosCompradorNF.presencaComprador}
                              onChange={e => setDadosCompradorNF(p => ({ ...p, presencaComprador: e.target.value }))}
                              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '13px' }}>
                              <option value="1">1 – Presencial</option>
                              <option value="2">2 – Internet</option>
                              <option value="3">3 – Teleatendimento</option>
                              <option value="9">9 – Outros</option>
                            </select>
                          </div>
                        </div>

                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#333', marginBottom: '16px' }}>
                          <input type="checkbox" checked={dadosCompradorNF.consumidorFinal}
                            onChange={e => setDadosCompradorNF(p => ({ ...p, consumidorFinal: e.target.checked }))}
                            style={{ width: '16px', height: '16px', accentColor: '#1976d2' }} />
                          <strong>Consumidor Final</strong> — marque se o comprador é o destinatário final do produto
                        </label>

                        {/* Botão salvar dados do comprador */}
                        <button
                          type="button"
                          disabled={salvandoComprador}
                          onClick={async () => {
                            const vendaId = dadosVisualizar?.VendaId;
                            if (!vendaId) { alert('Venda não vinculada. Não é possível salvar dados do comprador.'); return; }
                            setSalvandoComprador(true);
                            try {
                              const payload = {
                                VendaId: vendaId,
                                TipoDocumento: dadosCompradorNF.tipoDocumento,
                                NumeroDocumento: (dadosCompradorNF.numeroDocumento || '').replace(/\D/g, ''),
                                NomeRazaoSocial: dadosCompradorNF.nomeRazaoSocial || '',
                                Logradouro: dadosCompradorNF.logradouro || 'Não informado',
                                Numero: dadosCompradorNF.numero || 'S/N',
                                Complemento: dadosCompradorNF.complemento || null,
                                Bairro: dadosCompradorNF.bairro || 'Não informado',
                                CodigoMunicipioIBGE: dadosCompradorNF.codigoMunicipioIBGE || '0000000',
                                NomeMunicipio: dadosCompradorNF.nomeMunicipio || 'Não informado',
                                UF: dadosCompradorNF.uf || '',
                                CEP: (dadosCompradorNF.cep || '').replace(/\D/g, '') || '00000000',
                                CodigoPais: '1058',
                                NomePais: 'Brasil',
                                Telefone: dadosCompradorNF.telefone || null,
                                Email: dadosCompradorNF.email || null,
                                IndicadorIE: dadosCompradorNF.indicadorIE || '9',
                                InscricaoEstadual: dadosCompradorNF.inscricaoEstadual || null,
                                RegimeTributario: dadosCompradorNF.regimeTributario || 'SIMPLES',
                                TipoOperacao: 'VENDA_DENTRO_ESTADO',
                                ConsumidorFinal: dadosCompradorNF.consumidorFinal,
                                PresencaComprador: dadosCompradorNF.presencaComprador || '1',
                                FinalidadeNFe: '1'
                              };
                              if (compradorNFId) {
                                await apiCall(`DadosCompradorVenda/${compradorNFId}`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ ...payload, Id: compradorNFId })
                                });
                              } else {
                                const resp = await apiCall('DadosCompradorVenda', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify(payload)
                                });
                                const novoId = resp?.id || resp?.Id;
                                if (novoId) setCompradorNFId(novoId);
                              }
                              alert('✅ Dados do comprador salvos com sucesso!');
                            } catch (err) {
                              console.error('Erro ao salvar dados do comprador:', err);
                              alert('❌ Erro ao salvar dados do comprador: ' + (err?.message || 'Erro desconhecido'));
                            } finally {
                              setSalvandoComprador(false);
                            }
                          }}
                          style={{
                            padding: '10px 24px',
                            backgroundColor: salvandoComprador ? '#999' : '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: salvandoComprador ? 'wait' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          {salvandoComprador ? '⏳ Salvando...' : '💾 Salvar Dados do Comprador'}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Botões do Footer do Modal */}
                <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16, paddingTop: 16, borderTop: '2px solid #f0f0f0' }}>
                  {modoEdicao ? (
                    <>
                      <button 
                        type="button" 
                        onClick={() => setModoEdicao(false)} 
                        style={{ 
                          padding: '12px 24px', 
                          backgroundColor: '#757575', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: 8, 
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer' 
                        }}
                      >
                        Cancelar
                      </button>
                      <button 
                        type="submit" 
                        style={{ 
                          padding: '12px 24px', 
                          backgroundColor: 'rgb(235, 133, 0)', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: 8, 
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer' 
                        }}
                      >
                        Salvar
                      </button>
                    </>
                  ) : (
                    <>
                      <button 
                        type="button" 
                        onClick={() => setModoEdicao(true)} 
                        style={{ 
                          padding: '12px 24px', 
                          backgroundColor: 'rgb(235, 133, 0)', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: 8, 
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer' 
                        }}
                      >
                        Editar Nota
                      </button>
                      <button 
                        type="button" 
                        onClick={() => { setVisualizarNF(false); setModoEdicao(false); }} 
                        style={{ 
                          padding: '12px 24px', 
                          backgroundColor: '#757575', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: 8, 
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer' 
                        }}
                      >
                        Fechar
                      </button>
                    </>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ========== SIDEBAR DE EMISSÃO DE NOTAS FISCAIS ========== */}
      {sidebarEmissaoAberto && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '400px',
          height: '100vh',
          backgroundColor: '#1a1a1a',
          zIndex: 99999999,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.3)'
        }}>
          {/* Header do Sidebar */}
          <div style={{
            padding: '30px 25px',
            borderBottom: '1px solid #333',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{
              color: 'rgb(235, 133, 0)',
              fontSize: '24px',
              fontWeight: 'bold',
              margin: 0
            }}>
              Notas Fiscais
            </h2>
            <button
              onClick={handleFecharSidebarEmissao}
              style={{ backgroundColor: 'transparent', border: '1px solid #555', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18, cursor: 'pointer', flexShrink: 0 }}
              title="Fechar"
            >
              ×
            </button>
          </div>

          {/* Conteúdo do Sidebar */}
          <div style={{
            flex: 1,
            padding: '30px 25px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            {/* Estado inicial - antes de emitir */}
            {!emissaoEmAndamento && !emissaoResultado && (
              <>
                <div style={{
                  color: 'white',
                  fontSize: '20px',
                  fontWeight: '600',
                  marginBottom: '10px',
                  textAlign: 'center'
                }}>
                  {notasParaEmitir.length} {notasParaEmitir.length === 1 ? 'Nota Fiscal' : 'Notas Fiscais'}
                </div>
                <div style={{
                  color: '#888',
                  fontSize: '14px',
                  textAlign: 'center'
                }}>
                  Pronto para emitir no SEFAZ
                </div>
              </>
            )}

            {/* Estado de emissão em andamento */}
            {emissaoEmAndamento && (
              <>
                <div style={{
                  color: 'rgb(235, 133, 0)',
                  fontSize: '20px',
                  fontWeight: '600',
                  marginBottom: '30px',
                  textAlign: 'center'
                }}>
                  Emitindo notas {emissaoProgresso.atual} de {emissaoProgresso.total}
                </div>
                {/* Spinner de carregamento */}
                <div style={{
                  width: '60px',
                  height: '60px',
                  border: '4px solid #333',
                  borderTop: '4px solid rgb(235, 133, 0)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
              </>
            )}

          </div>

          {/* Resultado final - acima do footer */}
          {emissaoResultado && !emissaoEmAndamento && (
            <div style={{
              padding: '0 25px 20px 25px',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              {/* Notas com sucesso */}
              {emissaoResultado.sucesso.length > 0 && (
                <div style={{
                  marginBottom: '10px',
                  textAlign: 'center'
                }}>
                  <span style={{
                    color: '#40c463',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}>
                    {emissaoResultado.sucesso.length} {emissaoResultado.sucesso.length === 1 ? 'nota enviada' : 'notas enviadas'} com sucesso
                  </span>
                </div>
              )}

              {/* Notas com erro */}
              {emissaoResultado.erro.length > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '15px'
                }}>
                  <span style={{
                    color: '#f85149',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}>
                    {emissaoResultado.erro.length} {emissaoResultado.erro.length === 1 ? 'nota com erro' : 'notas com erro'}
                  </span>
                  <button
                    onClick={handleVerNotasComErro}
                    style={{
                      backgroundColor: 'rgb(235, 133, 0)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Ver
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Footer do Sidebar */}
          <div style={{
            padding: '25px',
            borderTop: '1px solid #333',
            display: 'flex',
            gap: '15px'
          }}>
            {!emissaoEmAndamento && !emissaoResultado && (
              <>
                <button
                  onClick={handleEmitirNotas}
                  style={{
                    flex: 1,
                    backgroundColor: 'rgb(235, 133, 0)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '15px 20px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Emitir Notas
                </button>
                <button
                  onClick={handleFecharSidebarEmissao}
                  style={{
                    backgroundColor: '#666',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '15px 20px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
              </>
            )}

            {emissaoResultado && !emissaoEmAndamento && (
              <button
                onClick={handleFecharSidebarEmissao}
                style={{
                  flex: 1,
                  backgroundColor: 'rgb(235, 133, 0)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '15px 20px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Concluir
              </button>
            )}
          </div>
        </div>
      )}
      {/* Modal de escolha de formato para download */}
      {baixarModalAberto && (
        <div className="modal-overlay" onClick={handleFecharBaixarModal} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000 }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: '90%', maxWidth: 420, background: '#fff', borderRadius: 12, padding: 20 }}>
            <h3 style={{ margin: 0, color: '#333', fontSize: 18 }}>Baixar {notasParaBaixar.length} {notasParaBaixar.length === 1 ? 'nota' : 'notas'}</h3>
            <p style={{ color: '#666', marginTop: 8 }}>Escolha o formato para download:</p>

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={() => executarDownloadFormato('pdf')} style={{ flex: 1, padding: '12px 14px', backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600 }}>Baixar PDF</button>
              <button onClick={() => executarDownloadFormato('xml')} style={{ flex: 1, padding: '12px 14px', backgroundColor: '#757575', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600 }}>Baixar XML</button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button className="btn-cancelar-laranja" onClick={handleFecharBaixarModal} style={{ padding: '8px 12px', background: '#fff', border: '1px solid #ddd', borderRadius: 6 }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* ========== POPUP DE NOTAS COM ERRO ========== */}
      {popupErrosAberto && (
        <div className="modal-overlay" onClick={() => setPopupErrosAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999999999 }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: '90%', maxWidth: 900, background: '#fff', borderRadius: 12, maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: '2px solid #f44336', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff5f5' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>⚠️</span>
                <div>
                  <h3 style={{ margin: 0, color: '#d32f2f', fontSize: '18px' }}>Notas Fiscais com Erro</h3>
                  <span style={{ color: '#666', fontSize: '13px' }}>{notasComErro.length} {notasComErro.length === 1 ? 'nota' : 'notas'} com erro na emissão</span>
                </div>
              </div>
              <button
                onClick={() => setPopupErrosAberto(false)}
                style={{ backgroundColor: '#f44336', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}
              >
                ×
              </button>
            </div>

            {/* Lista de notas com erro */}
            <div style={{ padding: '16px 24px', overflowY: 'auto', flex: 1 }}>
              {notasComErro.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Nenhuma nota com erro encontrada.</div>
              ) : (
                <div>
                  {/* Header da lista */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '10px 0', borderBottom: '2px solid #e0e0e0', marginBottom: '4px' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '13px', color: '#666', minWidth: '60px' }}>ID</span>
                    <span style={{ fontWeight: 'bold', fontSize: '13px', color: '#666', minWidth: '100px' }}>Número</span>
                    <span style={{ fontWeight: 'bold', fontSize: '13px', color: '#666', flex: 1 }}>Cliente</span>
                    <span style={{ fontWeight: 'bold', fontSize: '13px', color: '#666', minWidth: '120px' }}>Valor</span>
                    <span style={{ fontWeight: 'bold', fontSize: '13px', color: '#666', minWidth: '90px' }}>Status</span>
                    <span style={{ fontWeight: 'bold', fontSize: '13px', color: '#666', minWidth: '60px' }}></span>
                  </div>

                  {/* Linhas das notas */}
                  {notasComErro.map((nota, idx) => (
                    <div
                      key={nota.id || idx}
                      onClick={() => handleAbrirDetalheErro(nota)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        padding: '14px 0',
                        borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fff5f5'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <span style={{ fontSize: '14px', color: '#333', minWidth: '60px', fontWeight: '500' }}>{nota.id || '-'}</span>
                      <span style={{ fontSize: '14px', color: '#333', minWidth: '100px' }}>{nota.numero || '-'}</span>
                      <span style={{ fontSize: '14px', color: '#333', flex: 1 }}>{nota.nomeCliente || '-'}</span>
                      <span style={{ fontSize: '14px', color: '#333', minWidth: '120px' }}>
                        {nota.valor != null ? `R$ ${Number(nota.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: '500', color: '#f44336', minWidth: '90px' }}>
                        {nota.status || 'Erro'}
                      </span>
                      <span style={{ minWidth: '60px', textAlign: 'center' }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleAbrirDetalheErro(nota); }}
                          style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                        >
                          Detalhes
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0', textAlign: 'right' }}>
              <button
                onClick={() => setPopupErrosAberto(false)}
                style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== POPUP DE DETALHE DA NOTA COM ERRO ========== */}
      {detalheErroAberto && (
        <div className="modal-overlay" onClick={() => setDetalheErroAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999999999 }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: '90%', maxWidth: 950, background: '#fff', borderRadius: 12, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: '2px solid #f44336', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff5f5' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>📋</span>
                <div>
                  <h3 style={{ margin: 0, color: '#d32f2f', fontSize: '18px' }}>
                    Detalhes da Nota {notaErroDetalhe?.numero ? `#${notaErroDetalhe.numero}` : (notaErroDetalhe?.id ? `(ID: ${notaErroDetalhe.id})` : '')}
                  </h3>
                  <span style={{ color: '#666', fontSize: '13px' }}>Informações fiscais e diagnóstico do erro</span>
                </div>
              </div>
              <button
                onClick={() => setDetalheErroAberto(false)}
                style={{ backgroundColor: '#f44336', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}
              >
                ×
              </button>
            </div>

            {detalheErroLoading ? (
              <div style={{ padding: '60px', textAlign: 'center', color: '#666' }}>Carregando detalhes da nota...</div>
            ) : notaErroDetalhe ? (
              <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
                
                {/* ===== SEÇÃO DE ERRO / LOG ===== */}
                <div style={{ marginBottom: '24px', borderRadius: '10px', border: '2px solid #f44336', overflow: 'hidden' }}>
                  <div style={{ padding: '14px 20px', background: '#d32f2f', color: 'white' }}>
                    <span style={{ fontWeight: 'bold', fontSize: '15px' }}>🚨 Log de Erro</span>
                  </div>
                  <div style={{ padding: '16px 20px', background: '#ffebee' }}>
                    {/* Mensagem principal do erro */}
                    <div style={{ marginBottom: '12px', padding: '12px 16px', background: '#fff', borderRadius: '8px', border: '1px solid #ffcdd2' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#d32f2f', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Mensagem do Erro</div>
                      <div style={{ fontSize: '14px', color: '#333', lineHeight: '1.5', wordBreak: 'break-word' }}>
                        {notaErroDetalhe.mensagemErro || 'Sem mensagem de erro disponível'}
                      </div>
                    </div>

                    {/* Código de status SEFAZ */}
                    {notaErroDetalhe.codStatusResposta && (
                      <div style={{ marginBottom: '12px', padding: '12px 16px', background: '#fff', borderRadius: '8px', border: '1px solid #ffcdd2' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#d32f2f', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Código Status SEFAZ</div>
                        <div style={{ fontSize: '14px', color: '#333' }}>{notaErroDetalhe.codStatusResposta}</div>
                      </div>
                    )}

                    {/* XMotivo da SEFAZ */}
                    {notaErroDetalhe.xMotivo && notaErroDetalhe.xMotivo !== notaErroDetalhe.mensagemErro && (
                      <div style={{ marginBottom: '12px', padding: '12px 16px', background: '#fff', borderRadius: '8px', border: '1px solid #ffcdd2' }}>
                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#d32f2f', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Motivo (SEFAZ)</div>
                        <div style={{ fontSize: '14px', color: '#333', lineHeight: '1.5' }}>{notaErroDetalhe.xMotivo}</div>
                      </div>
                    )}

                    {/* Diagnóstico automático */}
                    <div style={{ marginTop: '8px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: 'rgb(184, 98, 0)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📊 Diagnóstico</div>
                      {gerarDiagnosticoErro(notaErroDetalhe).map((diag, idx) => (
                        <div key={idx} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '10px 14px', background: '#fff', borderRadius: '8px', border: '1px solid #ffe0b2', marginBottom: '8px' }}>
                          <span style={{ fontSize: '20px', flexShrink: 0 }}>{diag.icone}</span>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#333', marginBottom: '4px' }}>{diag.titulo}</div>
                            <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.5' }}>{diag.descricao}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ===== ABAS: PRODUTO / VENDA / COMPRADOR ===== */}
                <div style={{ marginBottom: '20px' }}>
                  {/* Tab Headers */}
                  <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: 0 }}>
                    {[
                      { key: 'erro', label: '🧾 Dados Fiscais', cor: 'rgb(235, 133, 0)' },
                      { key: 'produto', label: '📦 Produto', cor: '#4caf50' },
                      { key: 'venda', label: '🛒 Venda', cor: '#2196f3' },
                      { key: 'comprador', label: '👤 Comprador', cor: '#9c27b0' },
                    ].map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => setAbaErroDetalhe(tab.key)}
                        style={{
                          flex: 1, padding: '12px 8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                          background: abaErroDetalhe === tab.key ? tab.cor : '#f5f5f5',
                          color: abaErroDetalhe === tab.key ? '#fff' : '#666',
                          borderBottom: abaErroDetalhe === tab.key ? `3px solid ${tab.cor}` : '3px solid transparent',
                          transition: 'all 0.2s', borderRadius: '8px 8px 0 0',
                        }}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* ===== ABA: DADOS FISCAIS ===== */}
                  {abaErroDetalhe === 'erro' && (
                    <div style={{ padding: '20px', background: '#fafafa', border: '1px solid #e0e0e0', borderTop: 'none', borderRadius: '0 0 10px 10px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        {[
                          { label: 'ID da Nota', valor: notaErroDetalhe.id },
                          { label: 'Número', valor: notaErroDetalhe.numero },
                          { label: 'Série', valor: notaErroDetalhe.serie },
                          { label: 'Status', valor: notaErroDetalhe.status, cor: '#f44336' },
                          { label: 'Chave de Acesso', valor: notaErroDetalhe.chaveAcesso },
                          { label: 'Protocolo Autorização', valor: notaErroDetalhe.protocoloAutorizacao },
                          { label: 'Data Emissão', valor: notaErroDetalhe.dataEmissao ? new Date(notaErroDetalhe.dataEmissao).toLocaleString('pt-BR') : '' },
                          { label: 'Ambiente', valor: notaErroDetalhe.ambiente === '1' ? 'Produção' : notaErroDetalhe.ambiente === '2' ? 'Homologação' : notaErroDetalhe.ambiente },
                          { label: 'Valor Total', valor: notaErroDetalhe.valorTotal != null ? `R$ ${Number(notaErroDetalhe.valorTotal).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '' },
                          { label: 'Venda ID', valor: notaErroDetalhe.vendaId ?? notaErroDetalhe.VendaId },
                        ].map((campo, idx) => (
                          <div key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#888', marginBottom: '4px', textTransform: 'uppercase' }}>{campo.label}</label>
                            <span style={{ fontSize: '14px', color: campo.cor || '#333', fontWeight: campo.cor ? '600' : '400', padding: '8px 12px', background: '#fff', borderRadius: '6px', border: '1px solid #e0e0e0', minHeight: '20px', wordBreak: 'break-all' }}>
                              {campo.valor || '-'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ===== ABA: PRODUTO ===== */}
                  {abaErroDetalhe === 'produto' && (
                    <div style={{ padding: '20px', background: '#fafafa', border: '1px solid #e0e0e0', borderTop: 'none', borderRadius: '0 0 10px 10px' }}>
                      {produtosVendaErro.length === 0 ? (
                        <div style={{ textAlign: 'center', color: '#999', padding: '40px' }}>Nenhum produto vinculado a esta venda.</div>
                      ) : (
                        <>
                          {/* Seletor de produto se houver mais de 1 */}
                          {produtosVendaErro.length > 1 && (
                            <div style={{ marginBottom: '16px' }}>
                              <label style={{ fontSize: '12px', fontWeight: '600', color: '#888', marginBottom: '4px', display: 'block', textTransform: 'uppercase' }}>Selecionar Produto</label>
                              <select
                                value={produtoSelecionadoIdx}
                                onChange={e => {
                                  const idx = Number(e.target.value);
                                  setProdutoSelecionadoIdx(idx);
                                  setProdutoErroForm({ ...produtosVendaErro[idx] });
                                }}
                                style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '14px' }}
                              >
                                {produtosVendaErro.map((p, i) => (
                                  <option key={i} value={i}>{p.nome ?? p.Nome ?? `Produto ${p.id ?? p.Id}`}</option>
                                ))}
                              </select>
                            </div>
                          )}

                          {produtoErroForm && (
                            <>
                              {/* Dados Básicos */}
                              <div style={{ marginBottom: '16px', padding: '14px 18px', background: '#e8f5e9', borderRadius: '8px', border: '1px solid #c8e6c9' }}>
                                <div style={{ fontWeight: '600', fontSize: '14px', color: '#2e7d32', marginBottom: '12px' }}>📋 Dados Básicos</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                  {[
                                    { label: 'Nome', campo: 'nome', alt: 'Nome' },
                                    { label: 'Preço (R$)', campo: 'preco', alt: 'Preco', bloqueado: true, tipo: 'number' },
                                    { label: 'Unidade', campo: 'unidade', alt: 'Unidade' },
                                    { label: 'Código de Barras', campo: 'codigoBarras', alt: 'CodigoBarras' },
                                    { label: 'Categoria', campo: 'categoria', alt: 'Categoria' },
                                    { label: 'Ativo', campo: 'ativo', alt: 'Ativo', tipo: 'checkbox' },
                                  ].map((f, idx) => (
                                    <div key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                                      <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '3px', textTransform: 'uppercase' }}>
                                        {f.label} {f.bloqueado && <span style={{ color: '#f44336', fontSize: '10px' }}>(bloqueado)</span>}
                                      </label>
                                      {f.tipo === 'checkbox' ? (
                                        <input type="checkbox" checked={produtoErroForm[f.campo] ?? produtoErroForm[f.alt] ?? false}
                                          onChange={e => setProdutoErroForm(prev => ({ ...prev, [f.campo]: e.target.checked, [f.alt]: e.target.checked }))}
                                          style={{ width: 20, height: 20 }} />
                                      ) : (
                                        <input type={f.tipo || 'text'} value={produtoErroForm[f.campo] ?? produtoErroForm[f.alt] ?? ''}
                                          disabled={f.bloqueado}
                                          onChange={e => setProdutoErroForm(prev => ({ ...prev, [f.campo]: f.tipo === 'number' ? parseFloat(e.target.value) || 0 : e.target.value, [f.alt]: f.tipo === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
                                          style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '13px', background: f.bloqueado ? '#eee' : '#fff' }} />
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Estoque (bloqueado) */}
                              <div style={{ marginBottom: '16px', padding: '14px 18px', background: '#fff3e0', borderRadius: '8px', border: '1px solid #ffe0b2' }}>
                                <div style={{ fontWeight: '600', fontSize: '14px', color: 'rgb(184, 98, 0)', marginBottom: '12px' }}>📊 Estoque <span style={{ fontSize: '11px', color: '#999', fontWeight: '400' }}>(campos bloqueados)</span></div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                                  {[
                                    { label: 'Estoque Físico', campo: 'estoqueFisico', alt: 'EstoqueFisico' },
                                    { label: 'Estoque Reservado', campo: 'estoqueReservado', alt: 'EstoqueReservado' },
                                    { label: 'Estoque Mínimo', campo: 'estoqueMinimo', alt: 'EstoqueMinimo' },
                                  ].map((f, idx) => (
                                    <div key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                                      <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '3px', textTransform: 'uppercase' }}>{f.label}</label>
                                      <input type="number" value={produtoErroForm[f.campo] ?? produtoErroForm[f.alt] ?? 0} disabled
                                        style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '13px', background: '#eee', color: '#999' }} />
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Dados Fiscais */}
                              <div style={{ marginBottom: '16px', padding: '14px 18px', background: '#e3f2fd', borderRadius: '8px', border: '1px solid #bbdefb' }}>
                                <div style={{ fontWeight: '600', fontSize: '14px', color: '#1565c0', marginBottom: '12px' }}>🏛️ Dados Fiscais</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                                  {[
                                    { label: 'NCM', campo: 'ncm', alt: 'NCM' },
                                    { label: 'GTIN', campo: 'gtin', alt: 'GTIN' },
                                    { label: 'CFOP', campo: 'cfop', alt: 'CFOP' },
                                    { label: 'CEST', campo: 'cest', alt: 'CEST' },
                                    { label: 'Unidade Tributária', campo: 'unidadeTributaria', alt: 'UnidadeTributaria' },
                                    { label: 'Origem Mercadoria', campo: 'origemMercadoria', alt: 'OrigemMercadoria', tipo: 'number' },
                                    { label: 'UF Produção', campo: 'ufProducao', alt: 'UFProducao' },
                                    { label: 'Código ANP', campo: 'codigoANP', alt: 'CodigoANP' },
                                    { label: 'Fator Conversão', campo: 'fatorConversao', alt: 'FatorConversao', tipo: 'number' },
                                  ].map((f, idx) => (
                                    <div key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                                      <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '3px', textTransform: 'uppercase' }}>{f.label}</label>
                                      <input type={f.tipo || 'text'} value={produtoErroForm[f.campo] ?? produtoErroForm[f.alt] ?? ''}
                                        onChange={e => setProdutoErroForm(prev => ({ ...prev, [f.campo]: f.tipo === 'number' ? parseFloat(e.target.value) || 0 : e.target.value, [f.alt]: f.tipo === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
                                        style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '13px', background: '#fff' }} />
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* ICMS */}
                              <div style={{ marginBottom: '16px', padding: '14px 18px', background: '#fce4ec', borderRadius: '8px', border: '1px solid #f8bbd0' }}>
                                <div style={{ fontWeight: '600', fontSize: '14px', color: '#c62828', marginBottom: '12px' }}>💰 ICMS</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                                  {[
                                    { label: 'Alíquota ICMS (%)', campo: 'aliquotaICMS', alt: 'AliquotaICMS', tipo: 'number' },
                                    { label: 'CST ICMS', campo: 'csT_ICMS', alt: 'CST_ICMS' },
                                    { label: 'CSOSN', campo: 'csosn', alt: 'CSOSN' },
                                    { label: 'Modalidade BC', campo: 'modalidadeBCICMS', alt: 'ModalidadeBCICMS', tipo: 'number' },
                                    { label: '% Redução BC', campo: 'percentualReducaoBC', alt: 'PercentualReducaoBC', tipo: 'number' },
                                    { label: 'Alíquota ICMS-ST (%)', campo: 'aliquotaICMS_ST', alt: 'AliquotaICMS_ST', tipo: 'number' },
                                    { label: 'MVA ST (%)', campo: 'mvA_ST', alt: 'MVA_ST', tipo: 'number' },
                                    { label: 'Alíquota FCP (%)', campo: 'aliquotaFCP', alt: 'AliquotaFCP', tipo: 'number' },
                                  ].map((f, idx) => (
                                    <div key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                                      <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '3px', textTransform: 'uppercase' }}>{f.label}</label>
                                      <input type={f.tipo || 'text'} value={produtoErroForm[f.campo] ?? produtoErroForm[f.alt] ?? ''}
                                        onChange={e => setProdutoErroForm(prev => ({ ...prev, [f.campo]: f.tipo === 'number' ? parseFloat(e.target.value) || 0 : e.target.value, [f.alt]: f.tipo === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
                                        style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '13px', background: '#fff' }} />
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* IPI / PIS / COFINS */}
                              <div style={{ marginBottom: '16px', padding: '14px 18px', background: '#f3e5f5', borderRadius: '8px', border: '1px solid #e1bee7' }}>
                                <div style={{ fontWeight: '600', fontSize: '14px', color: '#6a1b9a', marginBottom: '12px' }}>📊 IPI / PIS / COFINS</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                                  {[
                                    { label: 'Alíquota IPI (%)', campo: 'aliquotaIPI', alt: 'AliquotaIPI', tipo: 'number' },
                                    { label: 'CST IPI', campo: 'csT_IPI', alt: 'CST_IPI' },
                                    { label: 'Enquadramento IPI', campo: 'codigoEnquadramentoIPI', alt: 'CodigoEnquadramentoIPI' },
                                    { label: 'Alíquota PIS (%)', campo: 'aliquotaPIS', alt: 'AliquotaPIS', tipo: 'number' },
                                    { label: 'CST PIS', campo: 'csT_PIS', alt: 'CST_PIS' },
                                    { label: 'Alíquota COFINS (%)', campo: 'aliquotaCOFINS', alt: 'AliquotaCOFINS', tipo: 'number' },
                                    { label: 'CST COFINS', campo: 'csT_COFINS', alt: 'CST_COFINS' },
                                  ].map((f, idx) => (
                                    <div key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                                      <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '3px', textTransform: 'uppercase' }}>{f.label}</label>
                                      <input type={f.tipo || 'text'} value={produtoErroForm[f.campo] ?? produtoErroForm[f.alt] ?? ''}
                                        onChange={e => setProdutoErroForm(prev => ({ ...prev, [f.campo]: f.tipo === 'number' ? parseFloat(e.target.value) || 0 : e.target.value, [f.alt]: f.tipo === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
                                        style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '13px', background: '#fff' }} />
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Peso / Dimensões */}
                              <div style={{ marginBottom: '16px', padding: '14px 18px', background: '#efebe9', borderRadius: '8px', border: '1px solid #d7ccc8' }}>
                                <div style={{ fontWeight: '600', fontSize: '14px', color: '#4e342e', marginBottom: '12px' }}>⚖️ Peso e Dimensões</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '12px' }}>
                                  {[
                                    { label: 'Peso Líq. (kg)', campo: 'pesoLiquido', alt: 'PesoLiquido', tipo: 'number' },
                                    { label: 'Peso Bruto (kg)', campo: 'pesoBruto', alt: 'PesoBruto', tipo: 'number' },
                                    { label: 'Altura (cm)', campo: 'altura', alt: 'Altura', tipo: 'number' },
                                    { label: 'Largura (cm)', campo: 'largura', alt: 'Largura', tipo: 'number' },
                                    { label: 'Profundidade (cm)', campo: 'profundidade', alt: 'Profundidade', tipo: 'number' },
                                  ].map((f, idx) => (
                                    <div key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                                      <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '3px', textTransform: 'uppercase' }}>{f.label}</label>
                                      <input type="number" step="0.001" value={produtoErroForm[f.campo] ?? produtoErroForm[f.alt] ?? ''}
                                        onChange={e => setProdutoErroForm(prev => ({ ...prev, [f.campo]: parseFloat(e.target.value) || 0, [f.alt]: parseFloat(e.target.value) || 0 }))}
                                        style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '13px', background: '#fff' }} />
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Informações Adicionais */}
                              <div style={{ marginBottom: '16px', padding: '14px 18px', background: '#e8eaf6', borderRadius: '8px', border: '1px solid #c5cae9' }}>
                                <div style={{ fontWeight: '600', fontSize: '14px', color: '#283593', marginBottom: '12px' }}>📝 Informações Adicionais</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                  {[
                                    { label: 'Benefício Fiscal', campo: 'beneficioFiscal', alt: 'BeneficioFiscal' },
                                    { label: 'Indicador Escala', campo: 'indicadorEscalaRelevante', alt: 'IndicadorEscalaRelevante' },
                                  ].map((f, idx) => (
                                    <div key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                                      <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '3px', textTransform: 'uppercase' }}>{f.label}</label>
                                      <input type="text" value={produtoErroForm[f.campo] ?? produtoErroForm[f.alt] ?? ''}
                                        onChange={e => setProdutoErroForm(prev => ({ ...prev, [f.campo]: e.target.value, [f.alt]: e.target.value }))}
                                        style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '13px', background: '#fff' }} />
                                    </div>
                                  ))}
                                  <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column' }}>
                                    <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '3px', textTransform: 'uppercase' }}>Informações Adicionais do Produto</label>
                                    <textarea value={produtoErroForm.informacoesAdicionaisProduto ?? produtoErroForm.InformacoesAdicionaisProduto ?? ''}
                                      onChange={e => setProdutoErroForm(prev => ({ ...prev, informacoesAdicionaisProduto: e.target.value, InformacoesAdicionaisProduto: e.target.value }))}
                                      rows={3} style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '13px', background: '#fff', resize: 'vertical' }} />
                                  </div>
                                </div>
                              </div>

                              {/* Botão Salvar Produto */}
                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                                <button
                                  onClick={() => setProdutoErroForm({ ...produtosVendaErro[produtoSelecionadoIdx] })}
                                  style={{ backgroundColor: '#666', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
                                >
                                  Desfazer Alterações
                                </button>
                                <button
                                  onClick={handleSalvarProdutoErro}
                                  disabled={salvandoProdutoErro}
                                  style={{ backgroundColor: salvandoProdutoErro ? '#aaa' : '#4caf50', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '14px', fontWeight: '600', cursor: salvandoProdutoErro ? 'not-allowed' : 'pointer' }}
                                >
                                  {salvandoProdutoErro ? 'Salvando...' : '💾 Salvar Produto'}
                                </button>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* ===== ABA: VENDA ===== */}
                  {abaErroDetalhe === 'venda' && (
                    <div style={{ padding: '20px', background: '#fafafa', border: '1px solid #e0e0e0', borderTop: 'none', borderRadius: '0 0 10px 10px' }}>
                      {!vendaErroForm ? (
                        <div style={{ textAlign: 'center', color: '#999', padding: '40px' }}>Nenhuma venda vinculada a esta nota.</div>
                      ) : (
                        <>
                          {/* Dados da Venda */}
                          <div style={{ marginBottom: '16px', padding: '14px 18px', background: '#e3f2fd', borderRadius: '8px', border: '1px solid #bbdefb' }}>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#1565c0', marginBottom: '12px' }}>🛒 Dados da Venda</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                              {[
                                { label: 'ID da Venda', valor: vendaErroForm.id ?? vendaErroForm.Id },
                                { label: 'Orçamento ID', valor: vendaErroForm.orcamentoID ?? vendaErroForm.OrcamentoID },
                                { label: 'Cliente', valor: vendaErroForm.nomeCliente ?? vendaErroForm.NomeCliente },
                                { label: 'Vendedor', valor: vendaErroForm.nomeVendedor ?? vendaErroForm.NomeVendedor },
                                { label: 'Data da Venda', valor: vendaErroForm.dataVenda ?? vendaErroForm.DataVenda },
                                { label: 'Status', valor: vendaErroForm.statusVenda ?? vendaErroForm.StatusVenda },
                                { label: 'Tipo Venda', valor: vendaErroForm.tipoVendaNome ?? vendaErroForm.TipoVendaNome ?? vendaErroForm.tipoVendaNatureza ?? vendaErroForm.TipoVendaNatureza },
                                { label: 'Status Entrega', valor: vendaErroForm.statusEntrega ?? vendaErroForm.StatusEntrega },
                                { label: 'Campanha', valor: vendaErroForm.nomeCampanha ?? vendaErroForm.NomeCampanha },
                              ].map((campo, idx) => (
                                <div key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '3px', textTransform: 'uppercase' }}>{campo.label}</label>
                                  <span style={{ fontSize: '13px', color: '#333', padding: '8px 10px', background: '#fff', borderRadius: '6px', border: '1px solid #e0e0e0', minHeight: '18px' }}>
                                    {campo.valor || '-'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Valores */}
                          <div style={{ marginBottom: '16px', padding: '14px 18px', background: '#e8f5e9', borderRadius: '8px', border: '1px solid #c8e6c9' }}>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#2e7d32', marginBottom: '12px' }}>💰 Valores</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                              {[
                                { label: 'Valor Original', valor: vendaErroForm.valorOriginal ?? vendaErroForm.ValorOriginal },
                                { label: 'Desconto', valor: vendaErroForm.desconto ?? vendaErroForm.Desconto },
                                { label: 'Valor Venda', valor: vendaErroForm.valorVenda ?? vendaErroForm.ValorVenda },
                                { label: 'Valor Juros', valor: vendaErroForm.valorJuros ?? vendaErroForm.ValorJuros },
                                { label: 'Valor Final', valor: vendaErroForm.valorFinal ?? vendaErroForm.ValorFinal },
                                { label: 'Impostos', valor: vendaErroForm.impostos ?? vendaErroForm.Impostos },
                                { label: 'Qtd Parcelas', valor: vendaErroForm.quantidadeParcelas ?? vendaErroForm.QuantidadeParcelas },
                                { label: 'Valor Parcela', valor: vendaErroForm.valorParcela ?? vendaErroForm.ValorParcela },
                                { label: 'Tipo Pagamento', valor: vendaErroForm.descricaoTipoPagamento ?? vendaErroForm.DescricaoTipoPagamento },
                              ].map((campo, idx) => (
                                <div key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '3px', textTransform: 'uppercase' }}>{campo.label}</label>
                                  <span style={{ fontSize: '13px', color: '#333', padding: '8px 10px', background: '#fff', borderRadius: '6px', border: '1px solid #e0e0e0', minHeight: '18px' }}>
                                    {campo.valor != null ? (typeof campo.valor === 'number' ? `R$ ${Number(campo.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : campo.valor) : '-'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Impostos da Venda */}
                          <div style={{ marginBottom: '16px', padding: '14px 18px', background: '#fff3e0', borderRadius: '8px', border: '1px solid #ffe0b2' }}>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: 'rgb(184, 98, 0)', marginBottom: '12px' }}>🧾 Impostos da Venda</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '12px' }}>
                              {[
                                { label: 'ICMS', valor: vendaErroForm.valorICMS ?? vendaErroForm.ValorICMS },
                                { label: 'PIS', valor: vendaErroForm.valorPIS ?? vendaErroForm.ValorPIS },
                                { label: 'COFINS', valor: vendaErroForm.valorCOFINS ?? vendaErroForm.ValorCOFINS },
                                { label: 'IPI', valor: vendaErroForm.valorIPI ?? vendaErroForm.ValorIPI },
                                { label: 'ISS', valor: vendaErroForm.valorISS ?? vendaErroForm.ValorISS },
                              ].map((campo, idx) => (
                                <div key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '3px', textTransform: 'uppercase' }}>{campo.label}</label>
                                  <span style={{ fontSize: '13px', color: '#333', padding: '8px 10px', background: '#fff', borderRadius: '6px', border: '1px solid #e0e0e0', minHeight: '18px' }}>
                                    {campo.valor != null ? `R$ ${Number(campo.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Itens da Venda */}
                          {(() => {
                            const itens = vendaErroForm.items ?? vendaErroForm.Items ?? vendaErroForm.itens ?? vendaErroForm.Itens ?? [];
                            return itens.length > 0 ? (
                              <div style={{ marginBottom: '16px', padding: '14px 18px', background: '#f3e5f5', borderRadius: '8px', border: '1px solid #e1bee7' }}>
                                <div style={{ fontWeight: '600', fontSize: '14px', color: '#6a1b9a', marginBottom: '12px' }}>📦 Itens da Venda ({itens.length})</div>
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead>
                                      <tr style={{ background: '#e1bee7' }}>
                                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600' }}>Produto</th>
                                        <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: '600' }}>Qtd</th>
                                        <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600' }}>Valor Unit.</th>
                                        <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600' }}>Total</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {itens.map((item, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid #e1bee7', background: '#fff' }}>
                                          <td style={{ padding: '8px 10px' }}>{item.nomeProduto ?? item.NomeProduto ?? `Produto ${item.produtoId ?? item.ProdutoId}`}</td>
                                          <td style={{ padding: '8px 10px', textAlign: 'center' }}>{item.quantidade ?? item.Quantidade}</td>
                                          <td style={{ padding: '8px 10px', textAlign: 'right' }}>R$ {Number(item.valorUnitario ?? item.ValorUnitario ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                          <td style={{ padding: '8px 10px', textAlign: 'right' }}>R$ {Number(item.valorTotal ?? item.ValorTotal ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            ) : null;
                          })()}

                          {/* Dados de Emissão */}
                          <div style={{ padding: '14px 18px', background: '#eceff1', borderRadius: '8px', border: '1px solid #cfd8dc' }}>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#37474f', marginBottom: '12px' }}>📍 Dados de Emissão</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                              {[
                                { label: 'UF Emissor', valor: vendaErroForm.emissorUF ?? vendaErroForm.EmissorUF },
                                { label: 'Município Emissor', valor: vendaErroForm.emissorMunicipio ?? vendaErroForm.EmissorMunicipio },
                                { label: 'Cód. Município', valor: vendaErroForm.emissorCodigoMunicipio ?? vendaErroForm.EmissorCodigoMunicipio },
                                { label: 'Notas / Observações', valor: vendaErroForm.notas ?? vendaErroForm.Notas },
                                { label: 'Descrição Orçamento', valor: vendaErroForm.descricaoOrcamento ?? vendaErroForm.DescricaoOrcamento },
                              ].map((campo, idx) => (
                                <div key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '3px', textTransform: 'uppercase' }}>{campo.label}</label>
                                  <span style={{ fontSize: '13px', color: '#333', padding: '8px 10px', background: '#fff', borderRadius: '6px', border: '1px solid #e0e0e0', minHeight: '18px' }}>
                                    {campo.valor || '-'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* ===== ABA: COMPRADOR ===== */}
                  {abaErroDetalhe === 'comprador' && (
                    <div style={{ padding: '20px', background: '#fafafa', border: '1px solid #e0e0e0', borderTop: 'none', borderRadius: '0 0 10px 10px' }}>
                      {!compradorErroForm && !notaErroDetalhe.vendaId && !notaErroDetalhe.VendaId ? (
                        <div style={{ textAlign: 'center', color: '#999', padding: '40px' }}>Nenhuma venda vinculada - não é possível cadastrar comprador.</div>
                      ) : (
                        <>
                          {!compradorErroForm && (
                            <div style={{ textAlign: 'center', padding: '20px', marginBottom: '16px', background: '#fff3e0', borderRadius: '8px', border: '1px solid #ffe0b2' }}>
                              <p style={{ color: 'rgb(184, 98, 0)', margin: '0 0 12px', fontSize: '14px' }}>Nenhum dado de comprador cadastrado para esta venda.</p>
                              <button
                                onClick={() => setCompradorErroForm({
                                  id: null,
                                  vendaId: notaErroDetalhe.vendaId ?? notaErroDetalhe.VendaId,
                                  tipoDocumento: '', numeroDocumento: '', nomeRazaoSocial: '',
                                  logradouro: '', numero: '', complemento: '', bairro: '',
                                  codigoMunicipioIBGE: '', nomeMunicipio: '', uf: '', cep: '',
                                  telefone: '', email: '', inscricaoEstadual: '', inscricaoMunicipal: '', inscricaoSUFRAMA: '',
                                })}
                                style={{ backgroundColor: '#9c27b0', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
                              >
                                + Cadastrar Dados do Comprador
                              </button>
                            </div>
                          )}

                          {compradorErroForm && (
                            <>
                              {/* Identificação */}
                              <div style={{ marginBottom: '16px', padding: '14px 18px', background: '#f3e5f5', borderRadius: '8px', border: '1px solid #e1bee7' }}>
                                <div style={{ fontWeight: '600', fontSize: '14px', color: '#6a1b9a', marginBottom: '12px' }}>🪪 Identificação</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '3px', textTransform: 'uppercase' }}>Tipo Documento</label>
                                    <select value={compradorErroForm.tipoDocumento}
                                      onChange={e => setCompradorErroForm(prev => ({ ...prev, tipoDocumento: e.target.value }))}
                                      style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '13px', background: '#fff' }}>
                                      <option value="">Selecione</option>
                                      <option value="CPF">CPF</option>
                                      <option value="CNPJ">CNPJ</option>
                                    </select>
                                  </div>
                                  {[
                                    { label: 'Número Documento (CPF/CNPJ)', campo: 'numeroDocumento' },
                                    { label: 'Nome / Razão Social', campo: 'nomeRazaoSocial' },
                                  ].map((f, idx) => (
                                    <div key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                                      <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '3px', textTransform: 'uppercase' }}>{f.label}</label>
                                      <input type="text" value={compradorErroForm[f.campo] ?? ''}
                                        onChange={e => setCompradorErroForm(prev => ({ ...prev, [f.campo]: e.target.value }))}
                                        style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '13px', background: '#fff' }} />
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Endereço */}
                              <div style={{ marginBottom: '16px', padding: '14px 18px', background: '#e8f5e9', borderRadius: '8px', border: '1px solid #c8e6c9' }}>
                                <div style={{ fontWeight: '600', fontSize: '14px', color: '#2e7d32', marginBottom: '12px' }}>📍 Endereço</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px' }}>
                                  {[
                                    { label: 'Logradouro', campo: 'logradouro' },
                                    { label: 'Número', campo: 'numero' },
                                    { label: 'Complemento', campo: 'complemento' },
                                    { label: 'Bairro', campo: 'bairro' },
                                    { label: 'CEP', campo: 'cep' },
                                    { label: 'UF', campo: 'uf' },
                                    { label: 'Município', campo: 'nomeMunicipio' },
                                    { label: 'Cód. Município IBGE', campo: 'codigoMunicipioIBGE' },
                                  ].map((f, idx) => (
                                    <div key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                                      <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '3px', textTransform: 'uppercase' }}>{f.label}</label>
                                      <input type="text" value={compradorErroForm[f.campo] ?? ''}
                                        onChange={e => setCompradorErroForm(prev => ({ ...prev, [f.campo]: e.target.value }))}
                                        style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '13px', background: '#fff' }} />
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Contato e Inscrições */}
                              <div style={{ marginBottom: '16px', padding: '14px 18px', background: '#e3f2fd', borderRadius: '8px', border: '1px solid #bbdefb' }}>
                                <div style={{ fontWeight: '600', fontSize: '14px', color: '#1565c0', marginBottom: '12px' }}>📞 Contato e Inscrições</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                                  {[
                                    { label: 'Telefone', campo: 'telefone' },
                                    { label: 'E-mail', campo: 'email' },
                                    { label: 'Inscrição Estadual', campo: 'inscricaoEstadual' },
                                    { label: 'Inscrição Municipal', campo: 'inscricaoMunicipal' },
                                    { label: 'Inscrição SUFRAMA', campo: 'inscricaoSUFRAMA' },
                                  ].map((f, idx) => (
                                    <div key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                                      <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '3px', textTransform: 'uppercase' }}>{f.label}</label>
                                      <input type="text" value={compradorErroForm[f.campo] ?? ''}
                                        onChange={e => setCompradorErroForm(prev => ({ ...prev, [f.campo]: e.target.value }))}
                                        style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '13px', background: '#fff' }} />
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Botão Salvar Comprador */}
                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                                <button
                                  onClick={handleSalvarCompradorErro}
                                  disabled={salvandoCompradorErro}
                                  style={{ backgroundColor: salvandoCompradorErro ? '#aaa' : '#9c27b0', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '14px', fontWeight: '600', cursor: salvandoCompradorErro ? 'not-allowed' : 'pointer' }}
                                >
                                  {salvandoCompradorErro ? 'Salvando...' : '💾 Salvar Dados do Comprador'}
                                </button>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ padding: '60px', textAlign: 'center', color: '#666' }}>Não foi possível carregar os detalhes da nota.</div>
            )}

            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setDetalheErroAberto(false); }}
                style={{ backgroundColor: '#666', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
              >
                Voltar
              </button>
              <button
                onClick={() => setDetalheErroAberto(false)}
                style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      <LoadingOverlay isLoading={loading} message="Processando..." />
    </div>
  );
}
